import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import Shell from "@/components/Shell";
import { toast } from "sonner";
import { Plus, Trash2, Play, Pause, Ban, ExternalLink, Save, Mail, KeyRound, Pencil, ShieldAlert, Clock } from "lucide-react";
import GlobalPonderrLearn from "./ponderr_learn/GlobalPonderrLearn";
import Territories from "./global_admin/geo/Territories";
import GlobalMarketplace from "./global_admin/marketplace/GlobalMarketplace";
import GlobalNotifications from "./global_admin/notifications/GlobalNotifications";
import { GlobalPostsHub } from "./posts/PostsHub";
import PlatformSettings from "./global_admin/settings/PlatformSettings";
import OpenSchoolRoster from "./global_admin/OpenSchoolRoster";
import PonderrAgentRAG from "./global_admin/PonderrAgentRAG";
import MembershipManager from "./global_admin/MembershipManager";
import SiteSettings from "./global_admin/SiteSettings";
import Settlements from "./global_admin/settlements/Settlements";

const TABS = [
  {id:"site", label:"Site (Hero/Footer)"},
  {id:"features", label:"Features"},
  {id:"schools", label:"Partner Schools"},
  {id:"territories", label:"Territories"},
  {id:"marketplace", label:"Store & TV"},
  {id:"notifications", label:"Notifications"},
  {id:"posts", label:"Posts & Media"},
  {id:"settlements", label:"Settlements"},
  {id:"platform_settings", label:"Platform Settings"},
  {id:"admins", label:"School Admins"},
  {id:"open_school", label:"Open Schooling"},
  {id:"ponderr_agent", label:"Ponderr Agent"},
  {id:"site_settings", label:"Site Settings"},
  {id:"overrides", label:"Override Windows"},
  {id:"updates", label:"Updates / Webinars"},
  {id:"plans", label:"Membership Plans"},
  {id:"events_mod", label:"Events Module"},
  {id:"enquiries", label:"Enquiries"},
  {id:"xplore", label:"Elevate Assignments"},
  {id:"coding_hub", label:"Coding Hub"},
  {id:"lms", label:"Ponderr LMS"},
  {id:"kit_store", label:"Kit Store"},
  {id:"ponderr_learn", label:"PonderrLearn"},
  {id:"ponderr_tv", label:"PonderrTV"},
];

function ComingSoonGlobal({ title, blurb, tone }) {
  return (
    <div className={`nb-card p-8 ${tone} space-y-3`}>
      <span className="nb-badge bg-white text-xs">Coming Soon · Next Iteration</span>
      <h2 className="display text-3xl font-bold">{title}</h2>
      <p className="text-sm max-w-3xl">{blurb}</p>
      <div className="text-xs mono opacity-70">This console will let the Global Admin curate & broadcast content to every partner school.</div>
    </div>
  );
}

export default function GlobalDashboard() {
  const [tab, setTab] = useState("site");
  return (
    <Shell title="Global Admin" subtitle="Multi-tenant control" tabs={TABS} active={tab} onTab={setTab}>
      {tab==="site" && <Site/>}
      {tab==="features" && <Features/>}
      {tab==="schools" && <Schools/>}
      {tab==="territories" && <Territories/>}
      {tab==="marketplace" && <GlobalMarketplace/>}
      {tab==="notifications" && <GlobalNotifications/>}
      {tab==="posts" && <GlobalPostsHub/>}
      {tab==="settlements" && <Settlements/>}
      {tab==="platform_settings" && <PlatformSettings/>}
      {tab==="admins" && <SchoolAdmins/>}
      {tab==="open_school" && <OpenSchoolRoster/>}
      {tab==="ponderr_agent" && <PonderrAgentRAG/>}
      {tab==="site_settings" && <SiteSettings/>}
      {tab==="overrides" && <OverrideWindows/>}
      {tab==="updates" && <Updates/>}
      {tab==="plans" && <PlansHub/>}
      {tab==="events_mod" && <EventsModuleControls/>}
      {tab==="enquiries" && <Enquiries/>}
      {tab==="xplore" && <XploreAssign/>}
      {tab==="coding_hub" && <CodingHub/>}
      {tab==="lms" && <LMS/>}
      {tab==="kit_store" && <ComingSoonGlobal title="Kit Store" tone="bg-[#86EFAC]" blurb="Central catalog of academic kits — books, uniforms, calculators, lab kits — mapped to Grade & School. Global Admin controls SKUs, pricing tiers, and shipping partners. Partner schools opt-in per SKU."/>}
      {tab==="ponderr_learn" && <GlobalPonderrLearn/>}
      {tab==="ponderr_tv" && <ComingSoonGlobal title="PonderrTV" tone="bg-[#FCA5A5]" blurb="A curated video channel — school events, cultural highlights, competition wins, and expert educator explainers. Global Admin approves submissions and drives the master schedule."/>}
    </Shell>
  );
}

/* ============================================================
   Privileged Override Windows — enable/disable per school
============================================================ */
function OverrideWindows() {
  const [schools, setSchools] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [duration, setDuration] = useState(24);
  const FEATURES = [
    {id:"historical_receipts", label:"Historical Receipt Insertion",
     hint:"Allow School Admin to insert prior offline fee receipts."},
    {id:"student_attendance", label:"Student Attendance Back-edit",
     hint:"Allow Educators to mark student attendance for past dates."},
    {id:"staff_attendance", label:"Staff Attendance Back-edit",
     hint:"Allow School Admin to mark staff attendance for past dates."},
    {id:"historical_donations", label:"Historical Donations Edit/Delete",
     hint:"Unlock Edit/Delete on already-recorded donation entries."},
    {id:"historical_expenses", label:"Historical Expenses Edit/Delete",
     hint:"Unlock Edit/Delete on already-approved/rejected expenses."},
    {id:"historical_payroll", label:"Historical Payroll Edit/Delete",
     hint:"Unlock Edit/Delete on already-run payroll rows (any status)."},
    {id:"educator_attendance", label:"Educator Attendance Back-edit",
     hint:"Unlock the date selector inside the Educator Console's Attendance tab so educators can correct past attendance."},
  ];
  const load = async () => {
    const [s, o] = await Promise.all([api.get("/global/schools"), api.get("/global/overrides")]);
    setSchools(s.data); setOverrides(o.data);
  };
  useEffect(()=>{ load(); }, []);
  const find = (sid, feat) => overrides.find(o => o.school_id === sid && o.feature === feat);
  const isActive = (o) => o && o.enabled && (!o.expires_at || o.expires_at > new Date().toISOString());
  const toggle = async (school_id, feature, enable) => {
    try {
      await api.post("/global/overrides", { school_id, feature, enabled: enable, duration_hours: parseInt(duration||24) });
      toast.success(`${enable?"Enabled":"Disabled"} · ${feature}`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  return <div className="space-y-5">
    <div className="nb-card p-5 bg-[#FCA5A5] flex flex-wrap items-end gap-3">
      <div>
        <div className="display text-xl font-bold flex items-center gap-2">
          <ShieldAlert size={20}/>Privileged Override Windows
        </div>
        <p className="text-sm mt-1">Temporarily unlock elevated actions (historical receipts, past-date attendance) for a specific school.
          Each grant auto-expires after the chosen duration — you may also revoke early.</p>
      </div>
      <label className="ml-auto"><span className="nb-label">Grant Duration (hours)</span>
        <input type="number" min="1" max="168" className="nb-input mt-1 mono w-24"
               value={duration} onChange={e=>setDuration(e.target.value)}
               data-testid="ovr-duration"/></label>
    </div>

    <div className="nb-card p-0 overflow-x-auto" data-testid="overrides-table-wrap">
      <table className="nb-table min-w-[1200px]">
        <thead>
          <tr>
            <th>School</th>
            {FEATURES.map(f => <th key={f.id} className="text-center min-w-[220px]">{f.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {schools.map(s => (
            <tr key={s.school_id} data-testid={`ovr-row-${s.school_id}`}>
              <td className="font-bold">{s.name}<div className="text-[10px] mono">{s.school_id}</div></td>
              {FEATURES.map(f => {
                const o = find(s.school_id, f.id);
                const active = isActive(o);
                return (
                  <td key={f.id} className="text-center">
                    {active && (
                      <div className="nb-badge bg-[#86EFAC] mb-1 text-[10px]">
                        <Clock size={10} className="inline mr-1"/> until {(o.expires_at||"").slice(0,16).replace("T"," ")}
                      </div>
                    )}
                    <div className="flex gap-1 justify-center">
                      <button onClick={()=>toggle(s.school_id, f.id, true)}
                              className={`nb-btn ${active?"nb-btn-secondary":""} px-2 py-1 text-xs`}
                              data-testid={`ovr-on-${s.school_id}-${f.id}`}>
                        <Play size={10}/> {active?"Extend":"Enable"}
                      </button>
                      {active && (
                        <button onClick={()=>toggle(s.school_id, f.id, false)}
                                className="nb-btn nb-btn-danger px-2 py-1 text-xs"
                                data-testid={`ovr-off-${s.school_id}-${f.id}`}>
                          <Pause size={10}/> Revoke
                        </button>
                      )}
                    </div>
                    <div className="text-[9px] text-slate-500 mt-1">{f.hint}</div>
                  </td>
                );
              })}
            </tr>
          ))}
          {schools.length === 0 && <tr><td colSpan={FEATURES.length+1} className="text-center py-6 text-slate-500">No partner schools yet</td></tr>}
        </tbody>
      </table>
    </div>
  </div>;
}

function XploreAssign() {
  const [rows, setRows] = useState([]);
  const [cats, setCats] = useState([]);
  const [settings, setSettings] = useState({grades:[]});
  // Iter49 — pull Board / Subject dropdowns from Ponderr LMS masters.
  const [lmsBoards, setLmsBoards] = useState([]);
  const [lmsSubjects, setLmsSubjects] = useState([]);
  const empty = {title:"", category:"Reading Progress", board:"", grade:"", subject_id:"", description:"", url:"", due_at:""};
  const [f, setF] = useState(empty);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => api.get("/xplore/assignments").then(r=>setRows(r.data)), []);
  useEffect(() => {
    load();
    api.get("/xplore/categories").then(r=>setCats(r.data));
    api.get("/settings").then(r=>setSettings(r.data)).catch(()=>{});
    api.get("/global/boards").then(r=>setLmsBoards(r.data)).catch(()=>{});
    api.get("/global/lms/subjects").then(r=>setLmsSubjects(r.data)).catch(()=>{});
  }, [load]);
  // Backend model only persists {title, category, grade, description, url, due_at}.
  // We prefix the title with `[BOARD·SUBJECT] ` so the two new dropdown values
  // survive save without any backend schema change.
  const encodeTitle = (obj) => {
    const b = obj.board || "";
    const subj = lmsSubjects.find(s=>s.subject_id === obj.subject_id);
    const s = subj ? subj.code : "";
    const tag = (b || s) ? `[${[b,s].filter(Boolean).join("·")}] ` : "";
    return `${tag}${obj.title || ""}`.replace(/\s+/g," ").trim();
  };
  // Reverse: pull the prefix back out when opening an existing row for edit.
  const decodeRow = (x) => {
    const m = /^\[([^\]]+)\]\s*(.*)$/.exec(x.title || "");
    if (!m) return {...x, board:"", subject_id:"", title: x.title || ""};
    const parts = m[1].split("·");
    const b = parts[0] || "";
    const subCode = parts[1] || "";
    const subj = lmsSubjects.find(s => s.code === subCode);
    return {...x, board: b, subject_id: subj?.subject_id || "", title: m[2]};
  };
  const create = async (e) => {
    e.preventDefault();
    if (!f.board) return toast.error("Pick a board");
    if (!f.grade) return toast.error("Pick a grade");
    try {
      await api.post("/xplore/assignments", {
        title: encodeTitle(f),
        category: f.category, grade: f.grade,
        description: f.description || "", url: f.url || "",
        due_at: f.due_at ? new Date(f.due_at).toISOString() : null,
      });
      toast.success(`Assignment pushed to Grade ${f.grade} on ${f.board}`);
      setF({...empty, category:f.category, board:f.board, grade:f.grade, subject_id:f.subject_id}); load();
    } catch(err){ toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this assignment for all students?")) return;
    await api.delete(`/xplore/assignments/${id}`); load();
  };
  const startEdit = (x) => setEditing({
    ...decodeRow(x),
    due_at: x.due_at ? new Date(x.due_at).toISOString().slice(0, 16) : "",
  });
  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editing.grade) return toast.error("Pick a grade");
    try {
      await api.patch(`/xplore/assignments/${editing.assignment_id}`, {
        title: encodeTitle(editing), category: editing.category, grade: editing.grade,
        description: editing.description || "", url: editing.url || "",
        due_at: editing.due_at ? new Date(editing.due_at).toISOString() : null,
      });
      toast.success("Assignment updated for all students of this grade");
      setEditing(null); load();
    } catch(err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const DEFAULT_GRADES = ["Nursery","LKG","UKG","G01","G02","G03","G04","G05","G06","G07","G08","G09","G10","G11","G12"];
  const grades = settings.grades?.length ? settings.grades : DEFAULT_GRADES;
  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <form onSubmit={create} className="nb-card p-6 bg-[#C4B5FD] space-y-2 h-fit">
        <h3 className="display text-xl font-bold">Push Elevate Assignment</h3>
        <p className="text-xs">Broadcasts a Microsoft-Education-style task to every student in the chosen Board · Grade · Subject across all partner schools. Dropdowns pull from Ponderr LMS masters (Boards → Grades → Master Subjects).</p>
        <input className="nb-input" placeholder="Title (e.g. 'Fractions Reading Progress — Chapter 3')" required
               value={f.title} onChange={e=>setF({...f, title:e.target.value})} data-testid="xplore-title"/>
        <select className="nb-select" value={f.category}
                onChange={e=>setF({...f, category:e.target.value})} data-testid="xplore-category">
          {cats.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        {/* Iter49 — Board → Grade → Subject sequence, pulled from LMS masters */}
        <select className="nb-select" required value={f.board}
                onChange={e=>setF({...f, board:e.target.value})} data-testid="xplore-board">
          <option value="">Choose Board —</option>
          {lmsBoards.map(b => <option key={b.code} value={b.code}>{b.code} — {b.name}</option>)}
        </select>
        <select className="nb-select" required value={f.grade}
                onChange={e=>setF({...f, grade:e.target.value})} data-testid="xplore-grade">
          <option value="">Choose Grade —</option>
          {grades.map(g => <option key={g} value={g}>{g}</option>)}
        </select>
        <select className="nb-select" value={f.subject_id}
                onChange={e=>setF({...f, subject_id:e.target.value})} data-testid="xplore-subject">
          <option value="">Choose Subject (optional) —</option>
          {lmsSubjects.map(s => <option key={s.subject_id} value={s.subject_id}>{s.name} ({s.code})</option>)}
        </select>
        <input className="nb-input" placeholder="Link (Teams meeting / SharePoint / etc.)"
               value={f.url} onChange={e=>setF({...f, url:e.target.value})} data-testid="xplore-url"/>
        <textarea className="nb-textarea" placeholder="Description / instructions"
                  value={f.description} onChange={e=>setF({...f, description:e.target.value})}/>
        <label className="block">
          <span className="nb-label">Due date &amp; time</span>
          <input type="datetime-local" className="nb-input" value={f.due_at}
                 onChange={e=>setF({...f, due_at:e.target.value})} data-testid="xplore-due"/>
        </label>
        <button className="nb-btn w-full justify-center" data-testid="xplore-submit">
          <Plus size={16}/> Push Assignment
        </button>
      </form>
      <div className="lg:col-span-2 space-y-3">
        {rows.map(x => (
          <div key={x.assignment_id} className="nb-card p-4 bg-white flex items-start gap-3"
               data-testid={`xplore-row-${x.assignment_id}`}>
            <div className="nb-badge bg-[#FDE047] shrink-0">{x.category}</div>
            <div className="flex-1">
              <div className="flex justify-between items-start gap-2">
                <div>
                  <div className="font-bold display text-lg">{x.title}</div>
                  <div className="text-xs mono">Grade: {x.grade}
                    {x.due_at && <> · Due {new Date(x.due_at).toLocaleString()}</>}
                    {x.updated_at && <> · edited</>}
                  </div>
                </div>
                <div className="flex gap-1">
                  <button onClick={()=>startEdit(x)}
                          className="nb-btn nb-btn-secondary px-2 py-1 text-xs"
                          data-testid={`xplore-edit-${x.assignment_id}`}>
                    <Pencil size={12}/>
                  </button>
                  <button onClick={()=>del(x.assignment_id)}
                          className="nb-btn nb-btn-danger px-2 py-1 text-xs"
                          data-testid={`xplore-delete-${x.assignment_id}`}>
                    <Trash2 size={12}/>
                  </button>
                </div>
              </div>
              {x.description && <p className="text-sm mt-2">{x.description}</p>}
              {x.url && <a href={x.url} target="_blank" rel="noreferrer"
                            className="nb-btn nb-btn-secondary mt-2 inline-flex text-xs">
                Open link <ExternalLink size={12}/></a>}
            </div>
          </div>
        ))}
        {rows.length === 0 && (
          <div className="nb-card p-6 text-slate-500 text-sm">
            No Elevate assignments yet. Push your first Microsoft-Education task using the form.
          </div>
        )}
      </div>
      {editing && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4"
             onClick={()=>setEditing(null)}>
          <form onSubmit={saveEdit}
                className="nb-card p-6 bg-white max-w-lg w-full space-y-3"
                onClick={e=>e.stopPropagation()}
                data-testid="xplore-edit-modal">
            <h3 className="display text-xl font-bold">Edit Elevate Assignment</h3>
            <input className="nb-input" placeholder="Title" required
                   value={editing.title||""} onChange={e=>setEditing({...editing, title:e.target.value})}
                   data-testid="xplore-edit-title"/>
            <select className="nb-select" value={editing.category}
                    onChange={e=>setEditing({...editing, category:e.target.value})}>
              {cats.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <select className="nb-select" required value={editing.board||""}
                    onChange={e=>setEditing({...editing, board:e.target.value})}
                    data-testid="xplore-edit-board">
              <option value="">Choose Board —</option>
              {lmsBoards.map(b => <option key={b.code} value={b.code}>{b.code} — {b.name}</option>)}
            </select>
            <select className="nb-select" required value={editing.grade}
                    onChange={e=>setEditing({...editing, grade:e.target.value})}>
              <option value="">Choose Grade —</option>
              {grades.map(g => <option key={g} value={g}>{g}</option>)}
            </select>
            <select className="nb-select" value={editing.subject_id||""}
                    onChange={e=>setEditing({...editing, subject_id:e.target.value})}
                    data-testid="xplore-edit-subject">
              <option value="">Choose Subject (optional) —</option>
              {lmsSubjects.map(s => <option key={s.subject_id} value={s.subject_id}>{s.name} ({s.code})</option>)}
            </select>
            <input className="nb-input" placeholder="Link"
                   value={editing.url||""} onChange={e=>setEditing({...editing, url:e.target.value})}/>
            <textarea className="nb-textarea" placeholder="Description"
                      value={editing.description||""} onChange={e=>setEditing({...editing, description:e.target.value})}/>
            <label className="block"><span className="nb-label">Due</span>
              <input type="datetime-local" className="nb-input" value={editing.due_at||""}
                     onChange={e=>setEditing({...editing, due_at:e.target.value})}/></label>
            <div className="flex gap-2">
              <button type="button" onClick={()=>setEditing(null)}
                      className="nb-btn nb-btn-ghost flex-1 justify-center">Cancel</button>
              <button className="nb-btn flex-1 justify-center" data-testid="xplore-edit-save">Save</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

/* ============================================================
   Coding Hub — Code.org / MakeCode multi-board dispatch
============================================================ */
function CodingHub() {
  const [rows, setRows] = useState([]);
  const [providers, setProviders] = useState([]);
  const [boards, setBoards] = useState([]);
  const [settings, setSettings] = useState({grades:[]});
  const empty = {title:"", provider:"code_org", url:"", description:"", thumbnail_url:"", target_boards:[], grades:[], order:0};
  const [f, setF] = useState(empty);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => api.get("/coding/activities").then(r=>setRows(r.data)), []);
  useEffect(() => {
    load();
    api.get("/coding/providers").then(r=>setProviders(r.data)).catch(()=>{});
    api.get("/global/boards").then(r=>setBoards(r.data)).catch(()=>{});
    api.get("/settings").then(r=>setSettings(r.data)).catch(()=>{});
  }, [load]);
  const DEFAULT_GRADES = ["Nursery","LKG","UKG","G01","G02","G03","G04","G05","G06","G07","G08","G09","G10","G11","G12"];
  const grades = settings.grades?.length ? settings.grades : DEFAULT_GRADES;
  const toggle = (obj, setObj, key, val) => {
    const arr = obj[key] || [];
    setObj({...obj, [key]: arr.includes(val) ? arr.filter(x=>x!==val) : [...arr, val]});
  };
  const create = async (e) => {
    e.preventDefault();
    if (!f.url) return toast.error("Paste the activity link");
    try {
      await api.post("/coding/activities", {...f, order: Number(f.order)||0});
      toast.success("Coding activity dispatched");
      setF({...empty, provider:f.provider}); load();
    } catch(err){ toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this coding activity for all students?")) return;
    await api.delete(`/coding/activities/${id}`); load();
  };
  const saveEdit = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/coding/activities/${editing.activity_id}`, {
        title:editing.title, provider:editing.provider, url:editing.url,
        description:editing.description||"", thumbnail_url:editing.thumbnail_url||"",
        target_boards:editing.target_boards||[], grades:editing.grades||[],
        order:Number(editing.order)||0,
      });
      toast.success("Activity updated"); setEditing(null); load();
    } catch(err){ toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const provLabel = (id) => providers.find(p=>p.id===id)?.label || id;
  const chip = (active) => `px-2 py-1 text-xs nb-card cursor-pointer ${active?"!bg-[#FDE047] font-bold":"!bg-white"}`;
  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <form onSubmit={create} className="nb-card p-6 !bg-[#93C5FD] space-y-2 h-fit">
        <h3 className="display text-xl font-bold">Dispatch Coding Activity</h3>
        <p className="text-xs">Push a Code.org / MakeCode / Scratch activity to students. Leave Boards or Grades empty to reach <b>every</b> board / grade.</p>
        <input className="nb-input" placeholder="Title (e.g. 'Minecraft Hour of Code')" required
               value={f.title} onChange={e=>setF({...f, title:e.target.value})} data-testid="coding-title"/>
        <select className="nb-select" value={f.provider}
                onChange={e=>setF({...f, provider:e.target.value})} data-testid="coding-provider">
          {providers.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
        </select>
        <input className="nb-input" placeholder="Activity URL (https://…)" required
               value={f.url} onChange={e=>setF({...f, url:e.target.value})} data-testid="coding-url"/>
        <input className="nb-input" placeholder="Thumbnail image URL (optional)"
               value={f.thumbnail_url} onChange={e=>setF({...f, thumbnail_url:e.target.value})} data-testid="coding-thumb"/>
        <textarea className="nb-textarea" placeholder="Short description"
                  value={f.description} onChange={e=>setF({...f, description:e.target.value})}/>
        <div>
          <span className="nb-label">Target Boards (none = all)</span>
          <div className="flex flex-wrap gap-1.5 mt-1" data-testid="coding-boards">
            {boards.map(b => (
              <span key={b.code} onClick={()=>toggle(f,setF,"target_boards",b.code)}
                    className={chip(f.target_boards.includes(b.code))} data-testid={`coding-board-${b.code}`}>{b.code}</span>
            ))}
          </div>
        </div>
        <div>
          <span className="nb-label">Target Grades (none = all)</span>
          <div className="flex flex-wrap gap-1.5 mt-1" data-testid="coding-grades">
            {grades.map(g => (
              <span key={g} onClick={()=>toggle(f,setF,"grades",g)}
                    className={chip(f.grades.includes(g))} data-testid={`coding-grade-${g}`}>{g}</span>
            ))}
          </div>
        </div>
        <button className="nb-btn w-full justify-center" data-testid="coding-submit">
          <Plus size={16}/> Dispatch Activity
        </button>
      </form>
      <div className="lg:col-span-2 space-y-3">
        {rows.map(x => (
          <div key={x.activity_id} className="nb-card p-4 bg-white flex items-start gap-3"
               data-testid={`coding-row-${x.activity_id}`}>
            <div className="nb-badge bg-[#FDE047] shrink-0">{provLabel(x.provider)}</div>
            <div className="flex-1">
              <div className="flex justify-between items-start gap-2">
                <div>
                  <div className="font-bold display text-lg">{x.title}</div>
                  <div className="text-xs mono">
                    Boards: {x.target_boards?.length ? x.target_boards.join(", ") : "ALL"} ·
                    Grades: {x.grades?.length ? x.grades.join(", ") : "ALL"}
                  </div>
                </div>
                <div className="flex gap-1">
                  <button onClick={()=>setEditing({...x})}
                          className="nb-btn nb-btn-secondary px-2 py-1 text-xs"
                          data-testid={`coding-edit-${x.activity_id}`}><Pencil size={12}/></button>
                  <button onClick={()=>del(x.activity_id)}
                          className="nb-btn nb-btn-danger px-2 py-1 text-xs"
                          data-testid={`coding-delete-${x.activity_id}`}><Trash2 size={12}/></button>
                </div>
              </div>
              {x.description && <p className="text-sm mt-2">{x.description}</p>}
              {x.url && <a href={x.url} target="_blank" rel="noreferrer"
                            className="nb-btn nb-btn-secondary mt-2 inline-flex text-xs">
                Open link <ExternalLink size={12}/></a>}
            </div>
          </div>
        ))}
        {rows.length === 0 && (
          <div className="nb-card p-6 text-slate-500 text-sm">
            No coding activities yet. Dispatch your first Code.org / MakeCode task using the form.
          </div>
        )}
      </div>
      {editing && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" onClick={()=>setEditing(null)}>
          <form onSubmit={saveEdit} className="nb-card p-6 bg-white max-w-lg w-full space-y-3 max-h-[90vh] overflow-y-auto"
                onClick={e=>e.stopPropagation()} data-testid="coding-edit-modal">
            <h3 className="display text-xl font-bold">Edit Coding Activity</h3>
            <input className="nb-input" placeholder="Title" required
                   value={editing.title||""} onChange={e=>setEditing({...editing, title:e.target.value})}
                   data-testid="coding-edit-title"/>
            <select className="nb-select" value={editing.provider}
                    onChange={e=>setEditing({...editing, provider:e.target.value})} data-testid="coding-edit-provider">
              {providers.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
            <input className="nb-input" placeholder="Activity URL" required
                   value={editing.url||""} onChange={e=>setEditing({...editing, url:e.target.value})} data-testid="coding-edit-url"/>
            <input className="nb-input" placeholder="Thumbnail image URL (optional)"
                   value={editing.thumbnail_url||""} onChange={e=>setEditing({...editing, thumbnail_url:e.target.value})}/>
            <textarea className="nb-textarea" placeholder="Description"
                      value={editing.description||""} onChange={e=>setEditing({...editing, description:e.target.value})}/>
            <div>
              <span className="nb-label">Target Boards (none = all)</span>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {boards.map(b => (
                  <span key={b.code} onClick={()=>toggle(editing,setEditing,"target_boards",b.code)}
                        className={chip((editing.target_boards||[]).includes(b.code))}
                        data-testid={`coding-edit-board-${b.code}`}>{b.code}</span>
                ))}
              </div>
            </div>
            <div>
              <span className="nb-label">Target Grades (none = all)</span>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {grades.map(g => (
                  <span key={g} onClick={()=>toggle(editing,setEditing,"grades",g)}
                        className={chip((editing.grades||[]).includes(g))}
                        data-testid={`coding-edit-grade-${g}`}>{g}</span>
                ))}
              </div>
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={()=>setEditing(null)}
                      className="nb-btn nb-btn-ghost flex-1 justify-center">Cancel</button>
              <button className="nb-btn flex-1 justify-center" data-testid="coding-edit-save">Save</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function SchoolAdmins() {  const [rows, setRows] = useState([]);
  const load = useCallback(() => api.get("/global/school-admins").then(r=>setRows(r.data)), []);
  useEffect(()=>{ load(); }, [load]);
  const reset = async (u) => {
    const pw = window.prompt(`New password for ${u.email} (min 6 chars):`);
    if (!pw) return;
    try {
      await api.post(`/users/${u.user_id}/reset-password`, {new_password: pw});
      toast.success(`Password reset for ${u.email}`);
    } catch(e){ toast.error(e?.response?.data?.detail || "Reset failed"); }
  };
  return <div className="nb-card p-0 overflow-hidden">
    <div className="p-4 bg-[#FDE047] border-b-2 border-black">
      <h3 className="display text-xl font-bold">School Admins — Password Reset</h3>
      <p className="text-xs mt-1">Reset the login password for any partner school&apos;s admin. The user&apos;s existing sessions are terminated immediately.</p>
    </div>
    <table className="nb-table">
      <thead><tr><th>School</th><th>Admin Name</th><th>Email</th><th>Actions</th></tr></thead>
      <tbody>
        {rows.map(u=>(<tr key={u.user_id} data-testid={`admin-row-${u.user_id}`}>
          <td className="font-bold">{u.school_name}</td>
          <td>{u.name}</td>
          <td className="text-xs">{u.email}</td>
          <td>
            <button onClick={()=>reset(u)} className="nb-btn nb-btn-secondary px-3 py-1 text-xs"
                    data-testid={`reset-${u.user_id}`}>
              <KeyRound size={12}/> Reset Password
            </button>
          </td>
        </tr>))}
        {rows.length===0 && <tr><td colSpan={4} className="text-center py-6 text-slate-500">No school admins yet</td></tr>}
      </tbody>
    </table>
  </div>;
}

function Site() {
  const [f, setF] = useState(null);
  useEffect(()=>{ api.get("/global/site").then(r=>setF(r.data)); }, []);
  if (!f) return <div className="mono p-4">Loading…</div>;
  const set = (k,v)=>setF({...f,[k]:v});
  const save = async () => {
    try { await api.put("/global/site", f); toast.success("Site updated"); }
    catch(e){ toast.error(e?.response?.data?.detail||"Failed"); }
  };
  const Field = ({label, k, type="text", rows}) => rows
    ? <label className="block"><span className="nb-label">{label}</span><textarea rows={rows} className="nb-textarea" value={f[k]||""} onChange={e=>set(k,e.target.value)} data-testid={`site-${k}`}/></label>
    : <label className="block"><span className="nb-label">{label}</span><input type={type} className="nb-input" value={f[k]||""} onChange={e=>set(k,e.target.value)} data-testid={`site-${k}`}/></label>;
  return <div className="grid lg:grid-cols-2 gap-6">
    <div className="nb-card p-6 bg-[#FDE047] space-y-3">
      <h3 className="display text-xl font-bold">Hero Section</h3>
      <Field label="Badge text" k="hero_badge"/>
      <div className="grid grid-cols-2 gap-2">
        <Field label="Title (main)" k="hero_title_main"/>
        <Field label="Title (highlighted word)" k="hero_title_highlight"/>
      </div>
      <Field label="Subtitle" k="hero_subtitle" rows={3}/>
      <div className="grid grid-cols-2 gap-2">
        <Field label="Primary CTA label" k="hero_cta_primary"/>
        <Field label="Primary CTA link" k="hero_cta_primary_link"/>
        <Field label="Secondary CTA label" k="hero_cta_secondary"/>
        <Field label="Secondary CTA link" k="hero_cta_secondary_link"/>
      </div>
      <Field label="Hero image URL" k="hero_image_url"/>
    </div>
    <div className="space-y-6">
      <div className="nb-card p-6 bg-[#93C5FD] space-y-3">
        <h3 className="display text-xl font-bold">Section Titles</h3>
        <Field label="Features title" k="section_title_features"/>
        <Field label="Partner Schools title" k="section_title_schools"/>
        <Field label="Updates title" k="section_title_updates"/>
        <Field label="Plans title" k="section_title_plans"/>
      </div>
      <div className="nb-card p-6 bg-[#86EFAC] space-y-3">
        <h3 className="display text-xl font-bold">Footer &amp; Contact</h3>
        <Field label="Tagline" k="footer_tagline"/>
        <Field label="Address" k="footer_address"/>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Phone" k="footer_phone"/>
          <Field label="Email" k="footer_email"/>
        </div>
        <Field label="Facebook URL" k="social_facebook"/>
        <Field label="Instagram URL" k="social_instagram"/>
        <Field label="YouTube URL" k="social_youtube"/>
      </div>
      <button onClick={save} className="nb-btn w-full justify-center" data-testid="site-save"><Save size={16}/>Save Site Config</button>
    </div>
  </div>;
}

const FEAT_COLORS = [
  {v:"bg-[#93C5FD]", n:"Sky"},{v:"bg-[#86EFAC]", n:"Mint"},
  {v:"bg-[#FDE047]", n:"Sunshine"},{v:"bg-[#C4B5FD]", n:"Lavender"},
  {v:"bg-[#FCA5A5]", n:"Coral"},{v:"bg-white", n:"White"},
];

function Features() {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({title:"", description:"", bg_color:"bg-[#93C5FD]", image_url:"", order:0});
  const load = ()=> api.get("/global/features").then(r=>setRows(r.data));
  useEffect(()=>{ load(); }, []);
  const create = async (e) => { e.preventDefault();
    await api.post("/global/features", {...f, order: parseInt(f.order)||0});
    setF({title:"",description:"",bg_color:"bg-[#93C5FD]",image_url:"",order:0});
    toast.success("Feature added"); load();
  };
  const del = async (id) => { await api.delete(`/global/features/${id}`); load(); };
  return <div className="grid lg:grid-cols-3 gap-6">
    <form onSubmit={create} className="nb-card p-6 bg-[#C4B5FD] space-y-2 h-fit">
      <h3 className="display text-xl font-bold">Add Feature</h3>
      <input className="nb-input" placeholder="Title" required value={f.title} onChange={e=>setF({...f,title:e.target.value})} data-testid="feat-title"/>
      <textarea className="nb-textarea" placeholder="Description" value={f.description} onChange={e=>setF({...f,description:e.target.value})}/>
      <select className="nb-select" value={f.bg_color} onChange={e=>setF({...f,bg_color:e.target.value})}>
        {FEAT_COLORS.map(c=><option key={c.v} value={c.v}>{c.n}</option>)}
      </select>
      <input className="nb-input" placeholder="Image URL" value={f.image_url} onChange={e=>setF({...f,image_url:e.target.value})}/>
      <input type="number" className="nb-input" placeholder="Order" value={f.order} onChange={e=>setF({...f,order:e.target.value})}/>
      <button className="nb-btn w-full justify-center"><Plus size={16}/>Add</button>
    </form>
    <div className="lg:col-span-2 grid sm:grid-cols-2 gap-4">
      {rows.map(x=>(<div key={x.feature_id} className={`nb-card overflow-hidden ${x.bg_color}`}>
        {x.image_url && <img src={x.image_url} alt="" className="h-32 w-full object-cover border-b-2 border-black"/>}
        <div className="p-4">
          <div className="flex justify-between items-start">
            <div><span className="nb-label">ORDER {x.order}</span><h3 className="display text-lg font-bold mt-1">{x.title}</h3></div>
            <button onClick={()=>del(x.feature_id)} className="nb-btn nb-btn-danger px-2 py-1"><Trash2 size={12}/></button>
          </div>
          <p className="text-sm mt-1">{x.description}</p>
        </div>
      </div>))}
      {rows.length===0 && <div className="nb-card p-6 text-slate-500 text-sm sm:col-span-2">No features yet — landing page will use defaults. Add 4 cards for best look.</div>}
    </div>
  </div>;
}

function Enquiries() {
  const [rows, setRows] = useState([]);
  const load = ()=> api.get("/global/enquiries").then(r=>setRows(r.data));
  useEffect(()=>{ load(); }, []);
  const setStatus = async (id, status) => { await api.patch(`/global/enquiries/${id}`, {status}); toast.success(`Marked ${status}`); load(); };
  const del = async (id) => { if(!window.confirm("Delete enquiry?")) return; await api.delete(`/global/enquiries/${id}`); load(); };
  return <div className="nb-card p-0 overflow-hidden">
    <table className="nb-table"><thead><tr><th>Date</th><th>Name</th><th>Enquiry Type</th><th>Email</th><th>Phone</th><th>Message</th><th>Status</th><th>Actions</th></tr></thead><tbody>
      {rows.map(e=>{
        const d = e.created_at ? new Date(e.created_at) : null;
        return (<tr key={e.enquiry_id}>
        <td className="mono text-xs whitespace-nowrap">
          {d ? d.toLocaleString(undefined, {year:"numeric",month:"short",day:"2-digit",hour:"2-digit",minute:"2-digit"}) : "—"}
        </td>
        <td className="font-bold">{e.name}</td>
        <td className="text-xs"><span className="nb-badge bg-[#C4B5FD] whitespace-nowrap" data-testid={`enq-type-${e.enquiry_id}`}>{e.enquiry_type || "General"}</span></td>
        <td className="text-xs"><a href={`mailto:${e.email}`} className="underline">{e.email}</a></td>
        <td className="text-xs">{e.phone}</td>
        <td className="text-xs max-w-md">{e.message}</td>
        <td><span className={`nb-badge ${e.status==="new"?"bg-[#FDE047]":e.status==="contacted"?"bg-[#93C5FD]":"bg-[#86EFAC]"}`}>{e.status}</span></td>
        <td><div className="flex gap-1 flex-wrap">
          <button onClick={()=>setStatus(e.enquiry_id,"contacted")} className="nb-btn nb-btn-secondary px-2 py-1 text-xs"><Mail size={12}/>Contacted</button>
          <button onClick={()=>setStatus(e.enquiry_id,"closed")} className="nb-btn nb-btn-tertiary px-2 py-1 text-xs">Close</button>
          <button onClick={()=>del(e.enquiry_id)} className="nb-btn nb-btn-danger px-2 py-1 text-xs"><Trash2 size={12}/></button>
        </div></td>
      </tr>);})}
      {rows.length===0 && <tr><td colSpan={8} className="text-center text-slate-500 py-8">No enquiries yet.</td></tr>}
    </tbody></table>
  </div>;
}

function Schools() {
  const [rows, setRows] = useState([]);
  const [founders, setFounders] = useState({});  // school_id -> founder user
  const [plans, setPlans] = useState([]);
  const [boards, setBoards] = useState([]);
  const [modules, setModules] = useState([]);
  const [subPlans, setSubPlans] = useState([]);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [invMap, setInvMap] = useState({});
  const [editStates, setEditStates] = useState([]);
  const [editDistricts, setEditDistricts] = useState([]);
  const emptyForm = {name:"", contact_name:"", contact_email:"", contact_phone:"", admin_password:"admin123",
                     board_code:"", boards:[], plan_key:"STARTER_ERP", payment_mode:"MARK_PAID",
                     country_code:"IN", state:"", district:"", city:"", postal_code:""};
  const [f, setF] = useState(emptyForm);
  const load = async () => {
    setRows((await api.get("/global/schools")).data);
    const fs = (await api.get("/global/founders")).data;
    const map = {}; fs.forEach(x => map[x.school_id] = x); setFounders(map);
    setPlans((await api.get("/global/plans")).data);
    setBoards((await api.get("/global/boards")).data);
    setModules((await api.get("/global/modules")).data);
    api.get("/schools/plans").then(r=>setSubPlans(r.data)).catch(()=>{});
    api.get("/geo/countries").then(r=>setCountries(r.data)).catch(()=>{});
    api.get("/global-admin/invoices").then(r=>{
      const m={}; (r.data||[]).forEach(iv=>{ if(!m[iv.school_id]) m[iv.school_id]=iv; }); // sorted desc → first is latest
      setInvMap(m);
    }).catch(()=>{});
  };
  useEffect(()=>{ load(); }, []);
  useEffect(()=>{ setStates([]); setDistricts([]); if(f.country_code) api.get("/geo/states",{params:{country_code:f.country_code}}).then(r=>setStates(r.data)).catch(()=>{}); }, [f.country_code]);
  useEffect(()=>{ setDistricts([]); if(f.country_code&&f.state) api.get("/geo/districts",{params:{country_code:f.country_code,state:f.state}}).then(r=>setDistricts(r.data)).catch(()=>{}); }, [f.state]);
  const create = async (e) => { e.preventDefault();
    if(!f.postal_code || !f.state || !f.district){ toast.error("Select state, district and enter postal code"); return; }
    const payload = {...f, boards: f.boards, primary_board: f.boards[0] || null, board_code: f.boards[0] || ""};
    try { const r = await api.post("/global-admin/schools/provision", payload);
      toast.success(`School provisioned → ${r.data.status} · invoice ${r.data.status==="ACTIVE"?"PAID":"PENDING"}`);
      setF(emptyForm); load(); }
    catch(err){ toast.error(err?.response?.data?.detail||"Failed"); }
  };
  const updateStatus = async (sid, status) => {
    await api.patch(`/global/schools/${sid}`, {status}); toast.success(`Status: ${status}`); load();
  };
  const patchSchool = async (sid, patch) => {
    try { await api.patch(`/global/schools/${sid}`, patch); load(); }
    catch(e){ toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const toggleModule = (sid, current, code) => {
    const set = new Set(current || []);
    if (set.has(code)) set.delete(code); else set.add(code);
    patchSchool(sid, {allowed_modules: Array.from(set)});
  };
  const del = async (sid) => { if(!window.confirm("Delete school record? (Data preserved)")) return; await api.delete(`/global/schools/${sid}`); load(); };
  const [editSchool, setEditSchool] = useState(null);
  const [modulesOpen, setModulesOpen] = useState(null);
  useEffect(()=>{ if(editSchool?.country_code) api.get("/geo/states",{params:{country_code:editSchool.country_code}}).then(r=>setEditStates(r.data)).catch(()=>{}); else setEditStates([]); }, [editSchool?.country_code]);
  useEffect(()=>{ if(editSchool?.country_code&&editSchool?.state) api.get("/geo/districts",{params:{country_code:editSchool.country_code,state:editSchool.state}}).then(r=>setEditDistricts(r.data)).catch(()=>{}); else setEditDistricts([]); }, [editSchool?.country_code, editSchool?.state]);
  const markPaid = async (sid) => {
    try {
      const r = await api.post(`/global-admin/schools/${sid}/mark-paid`);
      setInvMap(m=>({...m,[sid]:{...(m[sid]||{}),status:"PAID",payment_mode:"MARK_PAID"}}));
      setRows(rs=>rs.map(x=>x.school_id===sid?{...x,status:r.data.status}:x));
      if(editSchool?.school_id===sid) setEditSchool(es=>({...es,status:r.data.status}));
      toast.success("Settlement marked PAID → school ACTIVE");
    } catch(e){ toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const saveSchoolEdit = async () => {
    try {
      const patch = {
        name: editSchool.name, address: editSchool.address,
        phone: editSchool.phone, email: editSchool.email,
        hero_image_url: editSchool.hero_image_url,
        plan_id: editSchool.plan_id, board_code: (editSchool.boards && editSchool.boards[0]) || editSchool.board_code,
        boards: editSchool.boards || undefined,
        primary_board: (editSchool.boards && editSchool.boards[0]) || undefined,
        domain: editSchool.domain,
        country_code: editSchool.country_code || undefined,
        state: editSchool.state || undefined,
        district: editSchool.district || undefined,
        city: editSchool.city || undefined,
        postal_code: editSchool.postal_code || undefined,
        plan_key: editSchool.plan_key || undefined,
        photo_url: editSchool.photo_url || undefined,
        max_students: editSchool.max_students ? parseInt(editSchool.max_students) : undefined,
      };
      const lng = editSchool._lng ?? editSchool.center_coordinates?.coordinates?.[0];
      const lat = editSchool._lat ?? editSchool.center_coordinates?.coordinates?.[1];
      if (lng !== "" && lat !== "" && lng != null && lat != null) {
        patch.center_coordinates = { type: "Point", coordinates: [parseFloat(lng), parseFloat(lat)] };
      }
      await api.patch(`/global/schools/${editSchool.school_id}`, patch);
      // Reactive in-place row update (no full-page reload).
      setRows(rs=>rs.map(x=>x.school_id===editSchool.school_id?{...x,...patch}:x));
      toast.success("School updated"); setEditSchool(null); load();
    } catch(e){ toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const upsertFounder = async (school) => {
    const existing = founders[school.school_id];
    const email = window.prompt(`Founder email for ${school.name}:`, existing?.email || "");
    if (!email) return;
    const name = window.prompt("Founder name:", existing?.name || `Founder · ${school.name}`);
    const password = window.prompt("Password (min 6):", existing ? "" : "founder123");
    if (!password) return;
    try {
      await api.post("/global/schools/founder",
        {school_id: school.school_id, email, name, password});
      toast.success(existing ? "Founder updated" : "Founder created");
      load();
    } catch(e){ toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const activateSchool = async (s) => {
    const mode = window.confirm(`Activate "${s.name}"?\n\nOK = Mark Paid (ACTIVE)\nCancel = Send Payment Link (PENDING_PAYMENT)`) ? "MARK_PAID" : "SEND_LINK";
    const email = window.prompt("Admin email for this school:", s.contact_email || s.email || "");
    if (!email) return;
    try {
      const r = await api.post(`/global-admin/schools/${s.school_id}/activate`,
        { payment_mode: mode, admin_email: email, admin_password: "admin123", plan_key: s.plan_key });
      toast.success(`${s.name} → ${r.data.status}${r.data.admin_created ? " (admin created, pw: admin123)" : ""}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return <div className="grid lg:grid-cols-3 gap-6">
    <form onSubmit={create} className="nb-card p-6 bg-[#C4B5FD] space-y-2 h-fit">
      <h3 className="display text-xl font-bold">Provision School</h3>
      <input className="nb-input" placeholder="School Name" required value={f.name} onChange={e=>setF({...f,name:e.target.value})} data-testid="sch-name"/>
      <div className="nb-label">Boards (select one or more — first = Primary)</div>
      <div className="flex flex-wrap gap-1.5 border-2 border-black rounded-md p-2 bg-white" data-testid="sch-boards-select">
        {boards.length===0 && <span className="text-xs opacity-50">Add boards in Boards Management</span>}
        {boards.map(b=>{
          const idx = f.boards.indexOf(b.code);
          const on = idx >= 0;
          return <button type="button" key={b.board_id} data-testid={`sch-board-opt-${b.code}`}
            onClick={()=>setF({...f, boards: on ? f.boards.filter(x=>x!==b.code) : [...f.boards, b.code]})}
            className={`px-2 py-0.5 rounded text-xs font-bold border-2 border-black ${on?"bg-black text-white":"bg-white"}`}>
            {b.code}{idx===0 && <span className="ml-1 text-[9px] bg-[#FDE047] text-black px-1 rounded">Primary</span>}
          </button>;
        })}
      </div>
      <select className="nb-select text-sm" value={f.plan_key} onChange={e=>setF({...f,plan_key:e.target.value})} data-testid="sch-plan-select">
        {subPlans.length===0 && <option value="STARTER_ERP">Starter</option>}
        {subPlans.map(p => <option key={p.plan_key} value={p.plan_key}>{p.name}</option>)}
      </select>
      <hr className="border-black border-2"/>
      <div className="nb-label">Campus Location</div>
      <div className="grid grid-cols-2 gap-2">
        <select className="nb-select text-sm" value={f.country_code} onChange={e=>setF({...f,country_code:e.target.value,state:"",district:""})} data-testid="sch-country">
          {countries.map(c=><option key={c.country_code} value={c.country_code}>{c.country_name}</option>)}
        </select>
        <select className="nb-select text-sm" value={f.state} onChange={e=>setF({...f,state:e.target.value,district:""})} disabled={!states.length} data-testid="sch-state">
          <option value="">State *</option>
          {states.map(s=><option key={s} value={s}>{s}</option>)}
        </select>
        <select className="nb-select text-sm" value={f.district} onChange={e=>setF({...f,district:e.target.value})} disabled={!districts.length} data-testid="sch-district">
          <option value="">District *</option>
          {districts.map(d=><option key={d} value={d}>{d}</option>)}
        </select>
        <input className="nb-input text-sm" placeholder="City" value={f.city} onChange={e=>setF({...f,city:e.target.value})} data-testid="sch-city"/>
        <input className="nb-input text-sm mono col-span-2" placeholder="Postal / PIN code *" value={f.postal_code} onChange={e=>setF({...f,postal_code:e.target.value})} data-testid="sch-postal"/>
      </div>
      <hr className="border-black border-2"/>
      <div className="nb-label">Root Admin</div>
      <input className="nb-input" placeholder="Admin name" value={f.contact_name} onChange={e=>setF({...f,contact_name:e.target.value})} data-testid="sch-admin-name"/>
      <input className="nb-input" type="email" placeholder="Admin email" required value={f.contact_email} onChange={e=>setF({...f,contact_email:e.target.value})} data-testid="sch-admin-email"/>
      <div className="grid grid-cols-2 gap-2">
        <input className="nb-input" type="password" placeholder="Admin password" required minLength={6} value={f.admin_password} onChange={e=>setF({...f,admin_password:e.target.value})} data-testid="sch-admin-pw"/>
        <input className="nb-input mono" placeholder="Phone" value={f.contact_phone} onChange={e=>setF({...f,contact_phone:e.target.value})} data-testid="sch-phone"/>
      </div>
      <hr className="border-black border-2"/>
      <div className="nb-label">Settlement</div>
      <div className="grid grid-cols-1 gap-1.5">
        <label className={`flex items-start gap-2 p-2 rounded border-2 border-black cursor-pointer ${f.payment_mode==="MARK_PAID"?"bg-[#86EFAC]":"bg-white"}`} data-testid="sch-pay-mark-paid">
          <input type="radio" name="paymode" checked={f.payment_mode==="MARK_PAID"} onChange={()=>setF({...f,payment_mode:"MARK_PAID"})} className="mt-0.5"/>
          <span className="text-xs"><b>Direct Settlement (Mark Paid)</b><br/>School → <b>ACTIVE</b>, invoice <b>PAID</b>.</span>
        </label>
        <label className={`flex items-start gap-2 p-2 rounded border-2 border-black cursor-pointer ${f.payment_mode==="SEND_LINK"?"bg-[#FDE047]":"bg-white"}`} data-testid="sch-pay-send-link">
          <input type="radio" name="paymode" checked={f.payment_mode==="SEND_LINK"} onChange={()=>setF({...f,payment_mode:"SEND_LINK"})} className="mt-0.5"/>
          <span className="text-xs"><b>Send Payment Link</b><br/>School → <b>PENDING_PAYMENT</b>, invoice <b>PENDING</b>.</span>
        </label>
      </div>
      <button className="nb-btn w-full justify-center"><Plus size={16}/>Provision</button>
    </form>
    <div className="nb-card p-0 overflow-x-auto lg:col-span-2">
      <table className="nb-table"><thead><tr><th>School</th><th>Location</th><th>Portal Status</th><th>Payment</th><th style={{minWidth:140}}>Plan</th><th style={{minWidth:160}}>Board</th><th>Domain</th><th>Modules</th><th>Admin</th><th>Founder</th><th>Actions</th></tr></thead><tbody>
        {rows.map(s=>{
          const founder = founders[s.school_id];
          const allowedCount = (s.allowed_modules || []).length;
          const inv = invMap[s.school_id];
          const payStatus = inv?.status || (s.status==="active"||s.status==="ACTIVE" ? "PAID" : s.status==="PENDING_PAYMENT" ? "PENDING" : "—");
          const loc = [s.district, s.state, s.country_code].filter(Boolean).join(", ") || "—";
          return (<tr key={s.school_id}>
            <td className="font-bold">{s.name}<div className="text-xs text-slate-500">{s.city || s.address}</div></td>
            <td className="text-xs" data-testid={`sch-location-${s.school_id}`}>{loc}</td>
            <td><span className={`nb-badge ${s.status==="active"||s.status==="ACTIVE"?"bg-[#86EFAC]":s.status==="PENDING_APPROVAL"?"bg-[#93C5FD]":s.status==="PENDING_PAYMENT"||s.status==="paused"?"bg-[#FDE047]":"bg-[#FCA5A5]"}`} data-testid={`sch-status-${s.school_id}`}>{s.status}</span>
              {(s.status==="PENDING_APPROVAL"||s.status==="PENDING_PAYMENT") &&
                <button className="nb-btn text-[10px] mt-1 w-full justify-center" onClick={()=>activateSchool(s)} data-testid={`sch-activate-${s.school_id}`}>Review & Activate</button>}
            </td>
            <td><span className={`nb-badge ${payStatus==="PAID"?"bg-[#86EFAC]":payStatus==="PENDING"?"bg-[#FDE047]":"bg-slate-200"}`} data-testid={`sch-payment-${s.school_id}`}>{payStatus}</span>
              {payStatus!=="PAID" && <button className="nb-btn text-[10px] mt-1 w-full justify-center" onClick={()=>markPaid(s.school_id)} data-testid={`sch-markpaid-${s.school_id}`}>Mark Paid</button>}
            </td>
            <td style={{minWidth:140}}>
              <select className="nb-select text-xs w-full" value={s.plan_id || ""}
                      onChange={e=>patchSchool(s.school_id, {plan_id: e.target.value || null})}
                      data-testid={`sch-plan-${s.school_id}`}
                      style={{textOverflow:"ellipsis"}}>
                <option value="">—</option>
                {plans.map(p => <option key={p.plan_id} value={p.plan_id}>{p.name}</option>)}
              </select>
            </td>
            <td style={{minWidth:160}} data-testid={`sch-board-${s.school_id}`}>
              <div className="flex flex-wrap gap-1">
                {(s.boards && s.boards.length ? s.boards : (s.board_code ? [s.board_code] : [])).map((bc,i)=>(
                  <span key={bc} className={`nb-badge text-[10px] mono ${i===0?"bg-[#FDE047]":"bg-[#93C5FD]"}`}>
                    {bc}{i===0 && <span className="ml-1 font-bold">(Primary)</span>}
                  </span>
                ))}
                {!(s.boards && s.boards.length) && !s.board_code && <span className="text-xs opacity-50">—</span>}
              </div>
            </td>
            <td>
              <input className="nb-input text-xs" placeholder="school.example.com"
                     defaultValue={s.domain || ""}
                     onBlur={e=>{ if ((e.target.value||"") !== (s.domain||""))
                        patchSchool(s.school_id, {domain: e.target.value || null}); }}
                     data-testid={`sch-domain-${s.school_id}`} style={{minWidth:140}}/>
            </td>
            <td>
              <button className="nb-btn nb-btn-secondary px-2 py-1 text-xs"
                      onClick={()=>setModulesOpen(s)}
                      data-testid={`sch-modules-${s.school_id}`}>
                {allowedCount ? `${allowedCount} on` : "Set"}
              </button>
            </td>
            <td className="text-xs">{s.admin_email}</td>
            <td className="text-xs">
              {founder
                ? <div className="font-bold">{founder.email}</div>
                : <div className="text-slate-400 italic">Not set</div>}
              <button onClick={()=>upsertFounder(s)}
                      className="nb-btn nb-btn-secondary px-2 py-1 text-xs mt-1"
                      data-testid={`founder-btn-${s.school_id}`}>
                <KeyRound size={12}/> {founder ? "Reset" : "Add"} Founder
              </button>
            </td>
            <td><div className="flex gap-1 flex-wrap">
              <a href={`/school/${s.slug}`} target="_blank" rel="noreferrer" className="nb-btn nb-btn-ghost px-2 py-1 text-xs"><ExternalLink size={12}/>Portal</a>
              <button onClick={()=>setEditSchool({...s})}
                      className="nb-btn nb-btn-secondary px-2 py-1 text-xs"
                      data-testid={`sch-edit-${s.school_id}`}>
                <Pencil size={12}/> Edit
              </button>
              <button onClick={()=>updateStatus(s.school_id,"active")} className="nb-btn nb-btn-secondary px-2 py-1 text-xs"><Play size={12}/>Activate</button>
              <button onClick={()=>updateStatus(s.school_id,"paused")} className="nb-btn nb-btn-tertiary px-2 py-1 text-xs"><Pause size={12}/>Pause</button>
              <button onClick={()=>updateStatus(s.school_id,"suspended")} className="nb-btn nb-btn-danger px-2 py-1 text-xs"><Ban size={12}/>Suspend</button>
              <button onClick={()=>del(s.school_id)} className="nb-btn nb-btn-danger px-2 py-1 text-xs"><Trash2 size={12}/></button>
            </div></td>
          </tr>);
        })}
      </tbody></table>
    </div>
    {editSchool && (
      <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4"
           onClick={()=>setEditSchool(null)}>
        <div className="nb-card p-6 bg-white max-w-lg w-full space-y-2 max-h-[88vh] overflow-y-auto"
             onClick={e=>e.stopPropagation()} data-testid="sch-edit-drawer">
          <h3 className="display text-xl font-bold">Edit Partner School</h3>
          <label className="block"><span className="nb-label">Name</span>
            <input className="nb-input" value={editSchool.name||""}
                   onChange={e=>setEditSchool({...editSchool, name:e.target.value})}
                   data-testid="sch-edit-name"/></label>
          <label className="block"><span className="nb-label">Address</span>
            <textarea className="nb-textarea" rows={2} value={editSchool.address||""}
                      onChange={e=>setEditSchool({...editSchool, address:e.target.value})}/></label>
          <div className="grid grid-cols-2 gap-2">
            <label><span className="nb-label">Mobile / Phone</span>
              <input className="nb-input" value={editSchool.phone||""}
                     onChange={e=>setEditSchool({...editSchool, phone:e.target.value})}
                     data-testid="sch-edit-phone"/></label>
            <label><span className="nb-label">Public Email</span>
              <input className="nb-input" type="email" value={editSchool.email||""}
                     onChange={e=>setEditSchool({...editSchool, email:e.target.value})}/></label>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <label><span className="nb-label">Plan</span>
              <select className="nb-select" value={editSchool.plan_id||""}
                      onChange={e=>setEditSchool({...editSchool, plan_id:e.target.value})}>
                <option value="">—</option>
                {plans.map(p=><option key={p.plan_id} value={p.plan_id}>{p.name}</option>)}
              </select></label>
            <div><span className="nb-label">Boards (first = Primary)</span>
              <div className="flex flex-wrap gap-1.5 border-2 border-black rounded-md p-2 bg-white" data-testid="sch-edit-boards">
                {boards.map(b=>{
                  const cur = editSchool.boards && editSchool.boards.length ? editSchool.boards : (editSchool.board_code ? [editSchool.board_code] : []);
                  const idx = cur.indexOf(b.code); const on = idx>=0;
                  return <button type="button" key={b.board_id} data-testid={`sch-edit-board-${b.code}`}
                    onClick={()=>{ const next = on ? cur.filter(x=>x!==b.code) : [...cur, b.code];
                      setEditSchool({...editSchool, boards: next, board_code: next[0] || ""}); }}
                    className={`px-2 py-0.5 rounded text-xs font-bold border-2 border-black ${on?"bg-black text-white":"bg-white"}`}>
                    {b.code}{idx===0 && <span className="ml-1 text-[9px] bg-[#FDE047] text-black px-1 rounded">Primary</span>}
                  </button>;
                })}
              </div></div>
          </div>
          <label className="block"><span className="nb-label">School Domain</span>
            <input className="nb-input" placeholder="e.g. brightschool.example.com"
                   value={editSchool.domain||""}
                   onChange={e=>setEditSchool({...editSchool, domain:e.target.value})}
                   data-testid="sch-edit-domain"/></label>
          <label className="block"><span className="nb-label">Hero Image URL</span>
            <input className="nb-input" value={editSchool.hero_image_url||""}
                   onChange={e=>setEditSchool({...editSchool, hero_image_url:e.target.value})}
                   data-testid="sch-edit-hero"/></label>
          {editSchool.hero_image_url && (
            <img src={editSchool.hero_image_url} alt="hero preview"
                 className="w-full h-32 object-cover border-2 border-black rounded"/>
          )}
          <hr className="border-black border-2"/>
          <div className="nb-label">Billing & Settlement</div>
          {(()=>{ const inv=invMap[editSchool.school_id];
            const pay=inv?.status || (editSchool.status==="ACTIVE"||editSchool.status==="active"?"PAID":editSchool.status==="PENDING_PAYMENT"?"PENDING":"—");
            return (
            <div className="flex items-center justify-between gap-2 p-2 border-2 border-black rounded bg-slate-50" data-testid="sch-edit-settlement">
              <div className="text-xs">
                <div>Portal: <b>{editSchool.status}</b></div>
                <div>Payment: <span className={`nb-badge ${pay==="PAID"?"bg-[#86EFAC]":pay==="PENDING"?"bg-[#FDE047]":"bg-slate-200"}`}>{pay}</span>{inv?.payment_mode?<span className="ml-2 mono text-[10px] text-slate-500">· {inv.payment_mode}</span>:null}</div>
              </div>
              {pay!=="PAID" && <button onClick={()=>markPaid(editSchool.school_id)} className="nb-btn nb-btn-secondary text-xs" data-testid="sch-edit-markpaid">Mark Settlement Paid</button>}
            </div>);})()}
          <hr className="border-black border-2"/>
          <div className="nb-label">Campus Location</div>
          <div className="grid grid-cols-2 gap-2">
            <label><span className="nb-label text-[10px]">Country</span>
              <select className="nb-select" value={editSchool.country_code||""}
                      onChange={e=>setEditSchool({...editSchool, country_code:e.target.value, state:"", district:""})} data-testid="sch-edit-country">
                <option value="">—</option>
                {countries.map(c=><option key={c.country_code} value={c.country_code}>{c.country_name}</option>)}
              </select></label>
            <label><span className="nb-label text-[10px]">State</span>
              <select className="nb-select" value={editSchool.state||""} disabled={!editStates.length}
                      onChange={e=>setEditSchool({...editSchool, state:e.target.value, district:""})} data-testid="sch-edit-state">
                <option value="">{editSchool.state||"—"}</option>
                {editStates.map(s=><option key={s} value={s}>{s}</option>)}
              </select></label>
            <label><span className="nb-label text-[10px]">District</span>
              <select className="nb-select" value={editSchool.district||""} disabled={!editDistricts.length}
                      onChange={e=>setEditSchool({...editSchool, district:e.target.value})} data-testid="sch-edit-district">
                <option value="">{editSchool.district||"—"}</option>
                {editDistricts.map(d=><option key={d} value={d}>{d}</option>)}
              </select></label>
            <label><span className="nb-label text-[10px]">City</span>
              <input className="nb-input" value={editSchool.city||""}
                     onChange={e=>setEditSchool({...editSchool, city:e.target.value})} data-testid="sch-edit-city"/></label>
            <label><span className="nb-label text-[10px]">Postal Code</span>
              <input className="nb-input mono" value={editSchool.postal_code||""}
                     onChange={e=>setEditSchool({...editSchool, postal_code:e.target.value})} data-testid="sch-edit-postal"/></label>
            <label><span className="nb-label text-[10px]">Plan Key</span>
              <select className="nb-select" value={editSchool.plan_key||""}
                      onChange={e=>setEditSchool({...editSchool, plan_key:e.target.value})} data-testid="sch-edit-plankey">
                <option value="">—</option>
                <option value="STARTER_ERP">STARTER_ERP</option>
                <option value="GROWTH_ERP">GROWTH_ERP</option>
                <option value="ENTERPRISE_ERP">ENTERPRISE_ERP</option>
              </select></label>
            <label><span className="nb-label text-[10px]">Longitude</span>
              <input className="nb-input mono" value={editSchool._lng ?? (editSchool.center_coordinates?.coordinates?.[0] ?? "")}
                     onChange={e=>setEditSchool({...editSchool, _lng:e.target.value})} data-testid="sch-edit-lng"/></label>
            <label><span className="nb-label text-[10px]">Latitude</span>
              <input className="nb-input mono" value={editSchool._lat ?? (editSchool.center_coordinates?.coordinates?.[1] ?? "")}
                     onChange={e=>setEditSchool({...editSchool, _lat:e.target.value})} data-testid="sch-edit-lat"/></label>
            <label><span className="nb-label text-[10px]">Student Cap Override</span>
              <input className="nb-input mono" type="number" value={editSchool.max_students||""}
                     onChange={e=>setEditSchool({...editSchool, max_students:e.target.value})} data-testid="sch-edit-cap"/></label>
          </div>
          <label className="block"><span className="nb-label text-[10px]">Campus Photo URL</span>
            <input className="nb-input" value={editSchool.photo_url||""}
                   onChange={e=>setEditSchool({...editSchool, photo_url:e.target.value})} data-testid="sch-edit-photo"/></label>
          <div className="flex gap-2 pt-2">
            <button onClick={()=>setEditSchool(null)} className="nb-btn nb-btn-ghost flex-1 justify-center">Cancel</button>
            <button onClick={saveSchoolEdit} className="nb-btn flex-1 justify-center"
                    data-testid="sch-edit-save">Save Changes</button>
          </div>
        </div>
      </div>
    )}
    {modulesOpen && (
      <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4"
           onClick={()=>setModulesOpen(null)}>
        <div className="nb-card p-6 bg-white max-w-md w-full space-y-2 max-h-[80vh] overflow-y-auto"
             onClick={e=>e.stopPropagation()} data-testid="sch-modules-modal">
          <h3 className="display text-xl font-bold">Allowed Modules · {modulesOpen.name}</h3>
          <p className="text-xs text-slate-600">Tick each module this school can use.</p>
          <div className="space-y-1">
            {modules.length === 0 && <p className="text-xs italic">No modules defined yet. Create some on the Membership Plans tab.</p>}
            {modules.map(m => {
              const on = (modulesOpen.allowed_modules || []).includes(m.code);
              return <label key={m.module_id} className="flex items-center gap-2 border-2 border-black rounded p-2 cursor-pointer hover:bg-slate-50"
                            data-testid={`sch-module-toggle-${m.code}`}>
                <input type="checkbox" checked={on}
                       onChange={()=>{
                         toggleModule(modulesOpen.school_id, modulesOpen.allowed_modules, m.code);
                         setModulesOpen({...modulesOpen,
                           allowed_modules: on
                             ? (modulesOpen.allowed_modules||[]).filter(x=>x!==m.code)
                             : [...(modulesOpen.allowed_modules||[]), m.code]});
                       }}/>
                <div className="flex-1">
                  <div className="font-bold text-sm">{m.name} <span className="mono text-xs text-slate-500">({m.code})</span></div>
                  <div className="text-xs text-slate-500">{(m.tabs||[]).join(", ") || "no tabs"}</div>
                </div>
              </label>;
            })}
          </div>
          <button onClick={()=>setModulesOpen(null)} className="nb-btn w-full justify-center">Done</button>
        </div>
      </div>
    )}
  </div>;
}

const EMPTY_UPDATE = {
  post_type: "update", kind: "notice", title: "", body: "", link: "", image_url: "",
  meeting_provider: "google_meet", meeting_link: "", start_at: "", end_at: "",
  host_name: "", host_email: "", target_boards: [], target_grades: [],
  target_subjects: [], audience: "all", show_on_landing: true,
};

function MultiChips({ label, options, selected, onToggle, testid }) {
  return (
    <div data-testid={testid}>
      <span className="nb-label text-[11px]">{label}</span>
      <div className="flex flex-wrap gap-1 mt-1">
        {options.length === 0 && <span className="text-xs text-slate-500">None available</span>}
        {options.map((o) => {
          const on = selected.includes(o.value);
          return (
            <button type="button" key={o.value} onClick={() => onToggle(o.value)}
              className={`nb-btn text-[11px] px-2 py-0.5 ${on ? "" : "nb-btn-ghost"}`}
              data-testid={`${testid}-${o.value}`}>{o.label}</button>
          );
        })}
      </div>
    </div>
  );
}

function LiveSessionFields({ f, set }) {
  if (f.post_type === "update") return null;
  return (
    <div className="nb-card-sm p-3 bg-white/60 space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <label className="block"><span className="nb-label text-[11px]">Provider</span>
          <select className="nb-select mt-1" value={f.meeting_provider}
            onChange={(e) => set({ meeting_provider: e.target.value })} data-testid="upd-provider">
            <option value="google_meet">Google Meet</option>
            <option value="ms_teams">Microsoft Teams</option>
            <option value="zoom">Zoom</option>
            <option value="other">Other</option>
          </select></label>
        <label className="block"><span className="nb-label text-[11px]">Host Name</span>
          <input className="nb-input mt-1" value={f.host_name}
            onChange={(e) => set({ host_name: e.target.value })} data-testid="upd-host-name" /></label>
      </div>
      <label className="block"><span className="nb-label text-[11px]">Meeting Link (Meet / Teams / Zoom URL)</span>
        <input className="nb-input mt-1" placeholder="https://meet.google.com/…" value={f.meeting_link}
          onChange={(e) => set({ meeting_link: e.target.value })} data-testid="upd-meeting-link" /></label>
      <div className="grid grid-cols-2 gap-2">
        <label className="block"><span className="nb-label text-[11px]">Starts</span>
          <input type="datetime-local" className="nb-input mt-1" value={f.start_at || ""}
            onChange={(e) => set({ start_at: e.target.value })} data-testid="upd-start" /></label>
        <label className="block"><span className="nb-label text-[11px]">Ends</span>
          <input type="datetime-local" className="nb-input mt-1" value={f.end_at || ""}
            onChange={(e) => set({ end_at: e.target.value })} data-testid="upd-end" /></label>
      </div>
      <label className="block"><span className="nb-label text-[11px]">Host Email (optional)</span>
        <input className="nb-input mt-1" value={f.host_email}
          onChange={(e) => set({ host_email: e.target.value })} data-testid="upd-host-email" /></label>
    </div>
  );
}

function UpdateTargeting({ f, set, boards, grades, subjects }) {
  const toggle = (key, v) => {
    const arr = f[key] || [];
    set({ [key]: arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v] });
  };
  return (
    <div className="space-y-2">
      <label className="block"><span className="nb-label text-[11px]">Audience</span>
        <select className="nb-select mt-1" value={f.audience}
          onChange={(e) => set({ audience: e.target.value })} data-testid="upd-audience">
          <option value="all">Everyone</option>
          <option value="students">Students</option>
          <option value="educators">Educators</option>
          <option value="parents">Parents</option>
        </select></label>
      <MultiChips label="Target Boards (empty = all)" testid="upd-target-boards"
        options={boards.map((b) => ({ value: b.code, label: b.code }))}
        selected={f.target_boards || []} onToggle={(v) => toggle("target_boards", v)} />
      <MultiChips label="Target Grades (empty = all)" testid="upd-target-grades"
        options={grades.map((g) => ({ value: g, label: g }))}
        selected={f.target_grades || []} onToggle={(v) => toggle("target_grades", v)} />
      <MultiChips label="Target Subjects (empty = all)" testid="upd-target-subjects"
        options={subjects.map((s) => ({ value: s.name, label: s.name }))}
        selected={f.target_subjects || []} onToggle={(v) => toggle("target_subjects", v)} />
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={f.show_on_landing !== false}
          onChange={(e) => set({ show_on_landing: e.target.checked })} data-testid="upd-show-landing" />
        Show on Public Landing
      </label>
    </div>
  );
}

const toISO = (v) => (v ? new Date(v).toISOString() : null);
const toLocalInput = (v) => {
  if (!v) return "";
  const d = new Date(v);
  if (isNaN(d.getTime())) return "";
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

function Updates() {
  const lmsGrades = useLmsGrades();
  const [rows, setRows] = useState([]);
  const [boards, setBoards] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [f, setF] = useState(EMPTY_UPDATE);
  const [edit, setEdit] = useState(null);
  const load = ()=> api.get("/global/updates/manage").then(r=>setRows(r.data));
  useEffect(()=>{
    load();
    api.get("/global/boards").then(r=>setBoards(r.data)).catch(()=>{});
    api.get("/global/lms/subjects").then(r=>setSubjects(r.data)).catch(()=>{});
  }, []);
  const setFF = (patch) => setF((s) => ({ ...s, ...patch }));
  const withKind = (x) => ({ ...x, kind: x.post_type === "update" ? x.kind : "webinar",
    start_at: toISO(x.start_at), end_at: toISO(x.end_at) });
  const create = async (e) => {
    e.preventDefault();
    if ((f.post_type !== "update") && !f.meeting_link.trim()) return toast.error("Meeting link required for live sessions");
    try {
      await api.post("/global/updates", withKind(f));
      setF(EMPTY_UPDATE); toast.success("Published"); load();
    } catch(err){ toast.error(err?.response?.data?.detail || "Failed to publish"); }
  };
  const del = async (id) => { if(!window.confirm("Delete this update?")) return; await api.delete(`/global/updates/${id}`); load(); };
  const openEdit = (u) => setEdit({ ...EMPTY_UPDATE, ...u,
    start_at: toLocalInput(u.start_at), end_at: toLocalInput(u.end_at) });
  const saveEdit = async () => {
    try {
      await api.patch(`/global/updates/${edit.update_id}`, withKind({
        post_type: edit.post_type || "update", kind: edit.kind || "notice",
        title: edit.title, body: edit.body, link: edit.link || "", image_url: edit.image_url || "",
        meeting_provider: edit.meeting_provider || "google_meet", meeting_link: edit.meeting_link || "",
        start_at: edit.start_at || null, end_at: edit.end_at || null,
        host_name: edit.host_name || "", host_email: edit.host_email || "",
        target_boards: edit.target_boards || [], target_grades: edit.target_grades || [],
        target_subjects: edit.target_subjects || [], audience: edit.audience || "all",
        show_on_landing: edit.show_on_landing !== false,
      }));
      toast.success("Updated"); setEdit(null); load();
    } catch(e){ toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const setEF = (patch) => setEdit((s) => ({ ...s, ...patch }));
  const badge = (u) => (u.post_type && u.post_type !== "update") ? u.post_type.replace("_", " ").toUpperCase() : (u.kind || "news").toUpperCase();
  return <>
  <div className="grid lg:grid-cols-3 gap-6">
    <form onSubmit={create} className="nb-card p-6 bg-[#FDE047] space-y-2 h-fit">
      <h3 className="display text-xl font-bold">Publish Update / Webinar</h3>
      <label className="block"><span className="nb-label text-[11px]">Post Type</span>
        <select className="nb-select mt-1" value={f.post_type} onChange={e=>setFF({post_type:e.target.value})} data-testid="upd-post-type">
          <option value="update">Update / Notice</option>
          <option value="webinar">Webinar</option>
          <option value="live_class">Live Class</option>
        </select></label>
      {f.post_type === "update" && (
        <select className="nb-select" value={f.kind} onChange={e=>setFF({kind:e.target.value})} data-testid="upd-kind">
          <option value="notice">Notice</option><option value="quiz">Quiz</option><option value="webinar">Webinar</option>
        </select>
      )}
      <input className="nb-input" placeholder="Title" required value={f.title} onChange={e=>setFF({title:e.target.value})} data-testid="upd-title"/>
      <textarea className="nb-textarea" placeholder="Body" value={f.body} onChange={e=>setFF({body:e.target.value})}/>
      <LiveSessionFields f={f} set={setFF} />
      <input className="nb-input" placeholder="Link (optional)" value={f.link} onChange={e=>setFF({link:e.target.value})}/>
      <input className="nb-input" placeholder="Image URL" value={f.image_url} onChange={e=>setFF({image_url:e.target.value})}/>
      <UpdateTargeting f={f} set={setFF} boards={boards} grades={lmsGrades} subjects={subjects} />
      <button className="nb-btn w-full justify-center" data-testid="upd-publish"><Plus size={16}/>Publish</button>
    </form>
    <div className="lg:col-span-2 grid sm:grid-cols-2 gap-4 content-start">
      {rows.map(u=>(<div key={u.update_id} className="nb-card p-4 h-fit" data-testid={`upd-card-${u.update_id}`}>
        <div className="flex justify-between items-start">
          <span className="nb-badge">{badge(u)}</span>
          <div className="flex gap-1">
            <button onClick={()=>openEdit(u)} className="nb-btn nb-btn-secondary px-2 py-1 text-xs"
                    data-testid={`upd-edit-${u.update_id}`}>
              <Pencil size={12}/> Edit
            </button>
            <button onClick={()=>del(u.update_id)} className="nb-btn nb-btn-danger px-2 py-1"><Trash2 size={12}/></button>
          </div>
        </div>
        <h3 className="display text-lg font-bold mt-2">{u.title}</h3>
        <p className="text-sm mt-1">{u.body}</p>
        {(u.post_type && u.post_type !== "update") && (
          <div className="text-xs mono mt-2 space-y-0.5 text-slate-600">
            {u.start_at && <div>🕒 {new Date(u.start_at).toLocaleString()}</div>}
            {u.meeting_link && <div className="truncate">🔗 {u.meeting_provider}</div>}
            <div>🎯 {(u.target_grades?.length ? u.target_grades.join(", ") : "All grades")} · {u.audience || "all"}</div>
          </div>
        )}
      </div>))}
    </div>
  </div>
  {edit && (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4 overflow-y-auto"
         onClick={()=>setEdit(null)}>
      <div className="nb-card p-6 bg-white max-w-md w-full space-y-2 my-8"
           onClick={e=>e.stopPropagation()}>
        <h3 className="display text-xl font-bold">Edit Update</h3>
        <label className="block"><span className="nb-label text-[11px]">Post Type</span>
          <select className="nb-select mt-1" value={edit.post_type || "update"} onChange={e=>setEF({post_type:e.target.value})}>
            <option value="update">Update / Notice</option>
            <option value="webinar">Webinar</option>
            <option value="live_class">Live Class</option>
          </select></label>
        {(edit.post_type || "update") === "update" && (
          <select className="nb-select" value={edit.kind || "notice"} onChange={e=>setEF({kind:e.target.value})}>
            <option value="notice">Notice</option><option value="quiz">Quiz</option><option value="webinar">Webinar</option>
          </select>
        )}
        <input className="nb-input" placeholder="Title" value={edit.title||""} onChange={e=>setEF({title:e.target.value})}/>
        <textarea className="nb-textarea" placeholder="Body" value={edit.body||""} onChange={e=>setEF({body:e.target.value})}/>
        <LiveSessionFields f={{...edit, post_type: edit.post_type || "update"}} set={setEF} />
        <input className="nb-input" placeholder="Link" value={edit.link||""} onChange={e=>setEF({link:e.target.value})}/>
        <input className="nb-input" placeholder="Image URL" value={edit.image_url||""} onChange={e=>setEF({image_url:e.target.value})}/>
        <UpdateTargeting f={{...edit, target_boards: edit.target_boards||[], target_grades: edit.target_grades||[], target_subjects: edit.target_subjects||[]}} set={setEF} boards={boards} grades={lmsGrades} subjects={subjects} />
        <div className="flex gap-2 pt-2">
          <button onClick={()=>setEdit(null)} className="nb-btn nb-btn-ghost flex-1 justify-center">Cancel</button>
          <button onClick={saveEdit} className="nb-btn flex-1 justify-center" data-testid="upd-edit-save">Save</button>
        </div>
      </div>
    </div>
  )}
  </>;
}

function PlansHub() {
  const [sub, setSub] = useState("school");
  const SUBS = [
    ["school", "School Plans"],
    ["store", "Store Plans"],
    ["creator", "TV & Creator Plans"],
    ["franchise", "Franchise Plans"],
    ["open_student", "Open Student Plans"],
  ];
  return (
    <div className="space-y-4" data-testid="plans-hub">
      <div className="flex flex-wrap gap-2 border-b-2 border-black pb-3">
        {SUBS.map(([id, label]) => (
          <button key={id} data-testid={`plans-subtab-${id}`}
            className={`nb-btn text-xs sm:text-sm ${sub === id ? "" : "nb-btn-ghost"}`}
            onClick={() => setSub(id)}>{label}</button>
        ))}
      </div>
      {sub === "school" && <Plans/>}
      {sub === "store" && <MembershipManager category="store" accent="#86EFAC"
        note="Stores marketplace plans — read/write the existing store_plans collection. Editable price, discount, billing, trial, features & active state."/>}
      {sub === "creator" && <MembershipManager category="creator" accent="#C4B5FD"
        note="TV & Creator tiers — read/write the existing creator_subscription_plans collection (no duplicate collection)."/>}
      {sub === "franchise" && <MembershipManager category="franchise" accent="#FDE047"
        note="Franchise cluster plans (annual bundles: School + Store + Academies)."/>}
      {sub === "open_student" && <MembershipManager category="open_student" accent="#93C5FD"
        note="Direct-to-consumer Open Student passes shown on the Open School onboarding wizard."/>}
    </div>
  );
}

function Plans() {
  const [rows, setRows] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [f, setF] = useState({name:"", price_monthly:0, features:[], cta:"Get Started", highlight:false, storage_tier:"BASE"});
  const [feat, setFeat] = useState("");
  const [edit, setEdit] = useState(null);
  const [editFeat, setEditFeat] = useState("");
  const load = ()=> api.get("/global/plans").then(r=>setRows(r.data));
  useEffect(()=>{ load(); api.get("/global/storage-tiers").then(r=>setTiers(r.data)).catch(()=>{}); }, []);
  const tierLabel = (t)=>{ const o=tiers.find(x=>x.tier===(t||"BASE")); return o?`${o.tier} · ${o.limit_gb} GB`:(t||"BASE"); };
  const tierGb = (t)=>{ const o=tiers.find(x=>x.tier===(t||"BASE")); return o?`${o.limit_gb} GB`:"—"; };
  const create = async (e) => { e.preventDefault(); await api.post("/global/plans", {...f, price_monthly:parseFloat(f.price_monthly)}); setF({name:"",price_monthly:0,features:[],cta:"Get Started",highlight:false,storage_tier:"BASE"}); toast.success("Plan added"); load(); };
  const del = async (id) => { if(!window.confirm("Delete this plan?")) return; await api.delete(`/global/plans/${id}`); load(); };
  const saveEdit = async () => {
    try {
      await api.patch(`/global/plans/${edit.plan_id}`, {
        name: edit.name, price_monthly: parseFloat(edit.price_monthly),
        features: edit.features, cta: edit.cta, highlight: edit.highlight,
        storage_tier: edit.storage_tier || "BASE",
      });
      toast.success("Plan updated"); setEdit(null); load();
    } catch(e){ toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return <>
  <div className="grid lg:grid-cols-3 gap-6">
    <form onSubmit={create} className="nb-card p-6 bg-[#86EFAC] space-y-2 h-fit">
      <h3 className="display text-xl font-bold">Add Plan</h3>
      <input className="nb-input" placeholder="Plan name" required value={f.name} onChange={e=>setF({...f,name:e.target.value})}/>
      <input type="number" className="nb-input" placeholder="Monthly price ₹" required value={f.price_monthly} onChange={e=>setF({...f,price_monthly:e.target.value})}/>
      <label className="block">
        <span className="nb-label">Storage Allowance</span>
        <select className="nb-select mt-1" value={f.storage_tier} onChange={e=>setF({...f,storage_tier:e.target.value})} data-testid="plan-storage-tier">
          {tiers.map(t=><option key={t.tier} value={t.tier}>{t.tier} · {t.limit_gb} GB</option>)}
        </select>
      </label>
      <div className="flex gap-2"><input className="nb-input" placeholder="Feature" value={feat} onChange={e=>setFeat(e.target.value)} onKeyDown={e=>{if(e.key==='Enter'){e.preventDefault(); if(feat.trim()){setF({...f,features:[...f.features,feat.trim()]}); setFeat("");}}}}/><button type="button" onClick={()=>{if(feat.trim()){setF({...f,features:[...f.features,feat.trim()]}); setFeat("");}}} className="nb-btn">+</button></div>
      <div className="flex flex-wrap gap-1">{f.features.map((x,i)=><span key={`${x}-${i}`} className="nb-badge bg-white">{x} <button type="button" onClick={()=>setF({...f,features:f.features.filter((_,j)=>j!==i)})}>×</button></span>)}</div>
      <input className="nb-input" placeholder="CTA label" value={f.cta} onChange={e=>setF({...f,cta:e.target.value})}/>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.highlight} onChange={e=>setF({...f,highlight:e.target.checked})}/>Highlight as Most Popular</label>
      <button className="nb-btn w-full justify-center"><Plus size={16}/>Add</button>
    </form>
    <div className="lg:col-span-2 grid sm:grid-cols-3 gap-4">
      {rows.map(p=>(<div key={p.plan_id} className={`nb-card p-4 ${p.highlight?"bg-[#FDE047]":"bg-white"}`}>
        <div className="flex justify-between items-start gap-1">
          <h3 className="display text-lg font-bold">{p.name}</h3>
          <div className="flex gap-1">
            <button onClick={()=>setEdit({...p})} className="nb-btn nb-btn-secondary px-2 py-1 text-xs"
                    data-testid={`plan-edit-${p.plan_id}`}>
              <Pencil size={12}/>
            </button>
            <button onClick={()=>del(p.plan_id)} className="nb-btn nb-btn-danger px-2 py-1"><Trash2 size={12}/></button>
          </div>
        </div>
        <div className="display text-2xl font-black">₹{p.price_monthly.toLocaleString()}<span className="text-xs font-normal">/mo</span></div>
        <div className="nb-badge bg-[#93C5FD] mt-1 inline-block text-xs" data-testid={`plan-storage-${p.plan_id}`}>💾 {tierGb(p.storage_tier)} storage</div>
        <ul className="text-xs mt-2 space-y-1">{(p.features||[]).map((x,i)=><li key={`${p.plan_id}-${i}`}>✓ {x}</li>)}</ul>
      </div>))}
    </div>
  </div>
  {edit && (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4"
         onClick={()=>setEdit(null)}>
      <div className="nb-card p-6 bg-white max-w-md w-full space-y-2"
           onClick={e=>e.stopPropagation()}>
        <h3 className="display text-xl font-bold">Edit Plan</h3>
        <input className="nb-input" placeholder="Name" value={edit.name||""}
               onChange={e=>setEdit({...edit, name:e.target.value})}/>
        <input type="number" className="nb-input" placeholder="₹/mo" value={edit.price_monthly}
               onChange={e=>setEdit({...edit, price_monthly:e.target.value})}/>
        <label className="block">
          <span className="nb-label">Storage Allowance</span>
          <select className="nb-select mt-1" value={edit.storage_tier||"BASE"}
                  onChange={e=>setEdit({...edit, storage_tier:e.target.value})} data-testid="plan-edit-storage-tier">
            {tiers.map(t=><option key={t.tier} value={t.tier}>{t.tier} · {t.limit_gb} GB</option>)}
          </select>
        </label>
        <div className="flex gap-2">
          <input className="nb-input" placeholder="Feature" value={editFeat}
                 onChange={e=>setEditFeat(e.target.value)}
                 onKeyDown={e=>{if(e.key==='Enter'){e.preventDefault(); if(editFeat.trim()){setEdit({...edit, features:[...(edit.features||[]), editFeat.trim()]}); setEditFeat("");}}}}/>
          <button type="button"
                  onClick={()=>{if(editFeat.trim()){setEdit({...edit, features:[...(edit.features||[]), editFeat.trim()]}); setEditFeat("");}}}
                  className="nb-btn">+</button>
        </div>
        <div className="flex flex-wrap gap-1">
          {(edit.features||[]).map((x,i)=>(
            <span key={`${x}-${i}`} className="nb-badge bg-white">{x}
              <button type="button"
                      onClick={()=>setEdit({...edit, features:edit.features.filter((_,j)=>j!==i)})}>×</button>
            </span>
          ))}
        </div>
        <input className="nb-input" placeholder="CTA" value={edit.cta||""}
               onChange={e=>setEdit({...edit, cta:e.target.value})}/>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={!!edit.highlight}
                 onChange={e=>setEdit({...edit, highlight:e.target.checked})}/>Highlight
        </label>
        <div className="flex gap-2 pt-2">
          <button onClick={()=>setEdit(null)} className="nb-btn nb-btn-ghost flex-1 justify-center">Cancel</button>
          <button onClick={saveEdit} className="nb-btn flex-1 justify-center" data-testid="plan-edit-save">Save</button>
        </div>
      </div>
    </div>
  )}
  <ModuleManagement/>
  </>;
}

function BoardsManagement() {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({code:"", name:""});
  const [edit, setEdit] = useState(null);
  const load = ()=> api.get("/global/boards").then(r=>setRows(r.data));
  useEffect(()=>{ load(); }, []);
  const create = async (e) => { e.preventDefault();
    try { await api.post("/global/boards", f); toast.success("Board added");
          setF({code:"",name:""}); load();
    } catch(err){ toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const save = async () => { await api.patch(`/global/boards/${edit.board_id}`,
      {code: edit.code, name: edit.name}); toast.success("Updated"); setEdit(null); load(); };
  const del = async (id) => { if(!window.confirm("Delete board?")) return;
    await api.delete(`/global/boards/${id}`); load(); };
  return <div className="grid lg:grid-cols-3 gap-6">
    <form onSubmit={create} className="nb-card p-6 bg-[#FDE047] space-y-2 h-fit"
          data-testid="boards-mgmt-card">
      <h3 className="display text-xl font-bold">Boards Management</h3>
      <p className="text-xs">Central catalog of academic boards. Referenced by
        every Partner School.</p>
      <input className="nb-input" placeholder="Board Code (e.g. CBSE)" required
             value={f.code} onChange={e=>setF({...f,code:e.target.value})}
             data-testid="board-new-code"/>
      <input className="nb-input" placeholder="Board Name" required
             value={f.name} onChange={e=>setF({...f,name:e.target.value})}
             data-testid="board-new-name"/>
      <button className="nb-btn w-full justify-center" data-testid="board-new-submit">
        <Plus size={16}/>Add Board</button>
    </form>
    <div className="nb-card p-0 overflow-hidden lg:col-span-2">
      <table className="nb-table"><thead><tr><th>Code</th><th>Name</th><th>Actions</th></tr></thead>
      <tbody>
      {rows.map(b => <tr key={b.board_id} data-testid={`board-row-${b.code}`}>
        <td className="mono font-bold">{b.code}</td>
        <td>{b.name}</td>
        <td className="space-x-1 whitespace-nowrap">
          <button onClick={()=>setEdit({...b})} className="nb-btn nb-btn-secondary px-2 py-1 text-xs"
                  data-testid={`board-edit-${b.code}`}><Pencil size={12}/></button>
          <button onClick={()=>del(b.board_id)} className="nb-btn nb-btn-danger px-2 py-1 text-xs"
                  data-testid={`board-del-${b.code}`}><Trash2 size={12}/></button>
        </td>
      </tr>)}
      {rows.length===0 && <tr><td colSpan={3} className="text-center py-4 text-slate-500">No boards yet</td></tr>}
      </tbody></table>
    </div>
    {edit && (
      <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4"
           onClick={()=>setEdit(null)}>
        <div className="nb-card p-6 bg-white max-w-md w-full space-y-2"
             onClick={e=>e.stopPropagation()}>
          <h3 className="display text-xl font-bold">Edit Board</h3>
          <input className="nb-input" placeholder="Code" value={edit.code}
                 onChange={e=>setEdit({...edit,code:e.target.value})}/>
          <input className="nb-input" placeholder="Name" value={edit.name}
                 onChange={e=>setEdit({...edit,name:e.target.value})}/>
          <div className="flex gap-2 pt-2">
            <button onClick={()=>setEdit(null)} className="nb-btn nb-btn-ghost flex-1 justify-center">Cancel</button>
            <button onClick={save} className="nb-btn flex-1 justify-center"
                    data-testid="board-edit-save">Save</button>
          </div>
        </div>
      </div>
    )}
  </div>;
}

/* ==================================================================
   Iter28 — Ponderr LMS: Master Subjects + Curriculum + Course Builder
   ================================================================== */
const LMS_GRADES_FALLBACK = ["KG","G01","G02","G03","G04","G05","G06","G07","G08","G09","G10","G11","G12"];
// Iter50 — shared cache so every LMS dropdown uses the exact list from Grades Manager.
let _cachedLmsGrades = null;
export function useLmsGrades() {
  const [grades, setGrades] = useState(_cachedLmsGrades || LMS_GRADES_FALLBACK);
  useEffect(() => {
    if (_cachedLmsGrades) { setGrades(_cachedLmsGrades); return; }
    api.get("/global/master-grades")
      .then(r => {
        const codes = (r.data || []).map(g => g.code).filter(Boolean);
        if (codes.length) { _cachedLmsGrades = codes; setGrades(codes); }
      })
      .catch(() => {});
  }, []);
  return grades;
}

function MasterSubjectsManager() {
  const [rows, setRows] = useState([]);
  const [boards, setBoards] = useState([]);
  const [f, setF] = useState({code:"", name:"", icon:"", subject_type:"Scholastic", applicable_boards:[]});
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/global/lms/subjects").then(r=>setRows(r.data));
  useEffect(()=>{ load(); api.get("/global/boards").then(r=>setBoards(r.data)).catch(()=>{}); }, []);
  const toggleBoard = (obj, setter, code) => {
    const cur = obj.applicable_boards || [];
    setter({...obj, applicable_boards: cur.includes(code) ? cur.filter(x=>x!==code) : [...cur, code]});
  };
  const create = async (e) => { e.preventDefault();
    try { await api.post("/global/lms/subjects", f); toast.success("Subject added");
          setF({code:"",name:"",icon:"",subject_type:"Scholastic",applicable_boards:[]}); load();
    } catch(err){ toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const save = async () => {
    await api.patch(`/global/lms/subjects/${edit.subject_id}`,
      {code: edit.code, name: edit.name, icon: edit.icon || "",
       subject_type: edit.subject_type || "Scholastic",
       applicable_boards: edit.applicable_boards || []});
    toast.success("Subject updated"); setEdit(null); load();
  };
  const del = async (id) => { if(!window.confirm("Delete subject? All curriculums + lessons will be removed.")) return;
    await api.delete(`/global/lms/subjects/${id}`); toast.success("Deleted"); load();
  };
  const BoardChecks = ({obj, setter, scope="add"}) => (
    <div className="flex flex-wrap gap-1.5 border-2 border-black rounded-md p-2 bg-white max-h-28 overflow-y-auto" data-testid={scope==="edit"?"lms-subj-edit-boards":"lms-subj-boards"}>
      {boards.length===0 && <span className="text-xs opacity-50">No boards — add them in Boards Management</span>}
      {boards.map(b=>{
        const on = (obj.applicable_boards||[]).includes(b.code);
        return <button type="button" key={b.code} onClick={()=>toggleBoard(obj, setter, b.code)}
          className={`px-2 py-0.5 rounded text-xs font-bold border-2 border-black ${on?"bg-black text-white":"bg-white"}`}
          data-testid={`${scope==="edit"?"lms-subj-edit-board-":"lms-subj-board-"}${b.code}`}>{b.code}</button>;
      })}
    </div>
  );
  return <div className="grid lg:grid-cols-3 gap-6">
    <form onSubmit={create} className="nb-card p-6 bg-[#86EFAC] space-y-2 h-fit"
          data-testid="lms-subj-card">
      <h3 className="display text-xl font-bold">Master Subjects</h3>
      <p className="text-xs">Global subject catalog. Assigned per Board × Grade in the Curriculum Manager below.</p>
      <input className="nb-input" placeholder="Code (e.g. MATH)" required
             value={f.code} onChange={e=>setF({...f,code:e.target.value.toUpperCase()})}
             data-testid="lms-subj-new-code"/>
      <input className="nb-input" placeholder="Subject Name" required
             value={f.name} onChange={e=>setF({...f,name:e.target.value})}
             data-testid="lms-subj-new-name"/>
      <select className="nb-select" value={f.subject_type}
              onChange={e=>setF({...f,subject_type:e.target.value})} data-testid="lms-subj-new-type">
        <option value="Scholastic">Scholastic</option>
        <option value="Non-Scholastic">Non-Scholastic</option>
      </select>
      <label className="nb-label">Applicable Boards</label>
      <BoardChecks obj={f} setter={setF}/>
      <input className="nb-input" placeholder="Icon slug (optional)"
             value={f.icon} onChange={e=>setF({...f,icon:e.target.value})}/>
      <button className="nb-btn w-full justify-center" data-testid="lms-subj-new-submit">
        <Plus size={16}/>Add Subject</button>
    </form>
    <div className="nb-card p-0 overflow-hidden lg:col-span-2">
      <table className="nb-table"><thead><tr><th>Code</th><th>Name</th><th>Subject Type</th><th>Applicable Boards</th><th>Actions</th></tr></thead>
      <tbody>
      {rows.map(s => <tr key={s.subject_id} data-testid={`lms-subj-row-${s.code}`}>
        <td className="mono font-bold">{s.code}</td>
        <td>{s.name}</td>
        <td><span className={`nb-badge text-[10px] ${s.subject_type==="Non-Scholastic"?"bg-[#FBCFE8]":"bg-[#A7F3D0]"}`}>{s.subject_type || "Scholastic"}</span></td>
        <td className="space-x-1">{(s.applicable_boards||[]).length ? (s.applicable_boards||[]).map(b=><span key={b} className="nb-badge bg-[#93C5FD] text-[10px] mono">{b}</span>) : <span className="text-xs opacity-50">All</span>}</td>
        <td className="space-x-1 whitespace-nowrap">
          <button onClick={()=>setEdit({...s, applicable_boards:s.applicable_boards||[], subject_type:s.subject_type||"Scholastic"})} className="nb-btn nb-btn-secondary px-2 py-1 text-xs"
                  data-testid={`lms-subj-edit-${s.code}`}><Pencil size={12}/></button>
          <button onClick={()=>del(s.subject_id)} className="nb-btn nb-btn-danger px-2 py-1 text-xs"
                  data-testid={`lms-subj-del-${s.code}`}><Trash2 size={12}/></button>
        </td>
      </tr>)}
      {rows.length===0 && <tr><td colSpan={5} className="text-center py-4 text-slate-500">No subjects yet</td></tr>}
      </tbody></table>
    </div>
    {edit && (
      <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" onClick={()=>setEdit(null)}>
        <div className="nb-card p-6 bg-white max-w-md w-full space-y-2" onClick={e=>e.stopPropagation()}>
          <h3 className="display text-xl font-bold">Edit Subject</h3>
          <input className="nb-input" placeholder="Code" value={edit.code}
                 onChange={e=>setEdit({...edit,code:e.target.value.toUpperCase()})}/>
          <input className="nb-input" placeholder="Name" value={edit.name}
                 onChange={e=>setEdit({...edit,name:e.target.value})}/>
          <select className="nb-select" value={edit.subject_type||"Scholastic"}
                  onChange={e=>setEdit({...edit,subject_type:e.target.value})} data-testid="lms-subj-edit-type">
            <option value="Scholastic">Scholastic</option>
            <option value="Non-Scholastic">Non-Scholastic</option>
          </select>
          <label className="nb-label">Applicable Boards</label>
          <BoardChecks obj={edit} setter={setEdit} scope="edit"/>
          <input className="nb-input" placeholder="Icon" value={edit.icon||""}
                 onChange={e=>setEdit({...edit,icon:e.target.value})}/>
          <div className="flex gap-2 pt-2">
            <button onClick={()=>setEdit(null)} className="nb-btn nb-btn-ghost flex-1 justify-center">Cancel</button>
            <button onClick={save} className="nb-btn flex-1 justify-center" data-testid="lms-subj-edit-save">Save</button>
          </div>
        </div>
      </div>
    )}
  </div>;
}

function CurriculumManager() {
  const lmsGrades = useLmsGrades();
  const [boards, setBoards] = useState([]);
  const [subjects, setSubjects] = useState([]);
  // Iter30 — boardCodes is now an array (multi-select). Primary board = boardCodes[0].
  const [boardCodes, setBoardCodes] = useState([]);
  const [grade, setGrade] = useState("G10");
  const [subjectId, setSubjectId] = useState("");
  const [curriculum, setCurriculum] = useState(null); // primary board's curriculum
  const [chapterForm, setChapterForm] = useState({title:"", description:""});
  const [openChapter, setOpenChapter] = useState(null);
  const [editingChapter, setEditingChapter] = useState(null); // {chapter_id, title, description, order}
  const [boardsMenuOpen, setBoardsMenuOpen] = useState(false);
  const [wipeStep, setWipeStep] = useState(0); // 0=closed, 1=confirm, 2=type-to-confirm

  useEffect(()=>{
    api.get("/global/boards").then(r=>{ setBoards(r.data);
      if (r.data[0]) setBoardCodes(prev => prev.length ? prev : [r.data[0].code]); });
    api.get("/global/lms/subjects").then(r=>{ setSubjects(r.data);
      if (r.data[0]) setSubjectId(prev => prev || r.data[0].subject_id); });
  }, []);

  const primaryBoard = boardCodes[0];

  const loadCurriculum = useCallback(async () => {
    if (!primaryBoard || !grade || !subjectId) { setCurriculum(null); return; }
    const list = await api.get("/global/lms/curriculums", {
      params:{ board_code: primaryBoard, grade, subject_id: subjectId }});
    if (list.data && list.data[0]) {
      const detail = await api.get(`/global/lms/curriculums/${list.data[0].curriculum_id}`);
      setCurriculum(detail.data);
    } else { setCurriculum(null); }
  }, [primaryBoard, grade, subjectId]);

  useEffect(()=>{ loadCurriculum(); }, [loadCurriculum]);

  const toggleBoard = (code) => {
    setBoardCodes(prev => {
      const set = new Set(prev);
      if (set.has(code)) { set.delete(code); }
      else set.add(code);
      // Keep at least one board selected
      const next = [...set];
      return next.length ? next : [code];
    });
  };

  const addChapter = async (e) => {
    e.preventDefault();
    if (!chapterForm.title.trim()) return toast.error("Chapter title required");
    if (!boardCodes.length) return toast.error("Select at least one board");
    try {
      await api.post("/global/lms/multi/chapter", {
        board_codes: boardCodes, grade, subject_id: subjectId,
        title: chapterForm.title, description: chapterForm.description,
        order: (curriculum?.chapters?.length || 0) + 1,
      });
      setChapterForm({title:"", description:""});
      toast.success(`Chapter added to ${boardCodes.length} board(s)`);
      loadCurriculum();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const editChapter = (ch) => {
    // Iter48+ — open the same form used to Add Chapter, pre-filled.
    setEditingChapter({
      chapter_id: ch.chapter_id,
      title: ch.title || "",
      description: ch.description || "",
      order: ch.order || 0,
    });
  };
  const saveEditChapter = async (e) => {
    e.preventDefault();
    if (!editingChapter.title.trim()) return toast.error("Chapter title required");
    try {
      await api.patch(`/global/lms/multi/chapter/${editingChapter.chapter_id}`,
        { title: editingChapter.title,
          description: editingChapter.description || "",
          order: editingChapter.order || 0 });
      toast.success("Chapter updated across all mapped boards");
      setEditingChapter(null); loadCurriculum();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const delChapter = async (ch) => {
    if (!window.confirm(`Delete chapter "${ch.title}" and all its lessons across every mapped board?`)) return;
    await api.delete(`/global/lms/multi/chapter/${ch.chapter_id}`);
    toast.success("Chapter deleted"); loadCurriculum();
  };

  const performWipe = async () => {
    try {
      const r = await api.post("/global/lms/multi/wipe", {
        board_codes: boardCodes, grade, subject_id: subjectId });
      toast.success(`Wiped ${r.data.wiped} curriculum(s), removed ${r.data.lessons_removed} lesson(s)`);
      setWipeStep(0); loadCurriculum();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const subjectName = subjects.find(s => s.subject_id === subjectId)?.name || "";

  return <div className="nb-card p-6 bg-[#C4B5FD] space-y-4" data-testid="lms-curriculum-mgr">
    <div>
      <h3 className="display text-2xl font-bold">Curriculum Manager</h3>
      <p className="text-xs">Build an ordered chapter index for each Board × Grade × Subject.
        Populate lessons inside each chapter via the Course Builder.
        Multiple boards may be checked — chapters and lessons will be replicated across every selected board.</p>
    </div>
    <div className="grid md:grid-cols-3 gap-3">
      <div className="relative">
        <div className="nb-label">Boards ({boardCodes.length})</div>
        <button type="button"
                onClick={()=>setBoardsMenuOpen(x=>!x)}
                className="nb-select w-full text-left"
                data-testid="lms-cur-boards-toggle">
          {boardCodes.length === 0 ? "Pick boards…"
            : boardCodes.length === 1 ? boardCodes[0]
            : `${boardCodes.length} boards`}
        </button>
        {boardsMenuOpen && (
          <div className="absolute z-30 mt-1 w-full nb-card bg-white p-2 max-h-64 overflow-y-auto space-y-1"
               data-testid="lms-cur-boards-menu">
            {boards.map(b => (
              <label key={b.code} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-slate-100 p-1 rounded"
                     data-testid={`lms-cur-board-${b.code}`}>
                <input type="checkbox" checked={boardCodes.includes(b.code)}
                       onChange={()=>toggleBoard(b.code)}/>
                <span className="mono font-bold">{b.code}</span>
                <span className="truncate">{b.name}</span>
              </label>
            ))}
            <button type="button" onClick={()=>setBoardsMenuOpen(false)}
                    className="nb-btn nb-btn-ghost w-full mt-1 text-xs justify-center">Done</button>
          </div>
        )}
      </div>
      <div>
        <div className="nb-label">Grade</div>
        <select className="nb-select" value={grade} onChange={e=>setGrade(e.target.value)}
                data-testid="lms-cur-grade">
          {lmsGrades.map(g => <option key={g} value={g}>{g}</option>)}
        </select>
      </div>
      <div>
        <div className="nb-label">Subject</div>
        <select className="nb-select" value={subjectId} onChange={e=>setSubjectId(e.target.value)}
                data-testid="lms-cur-subject">
          {subjects.map(s => <option key={s.subject_id} value={s.subject_id}>{s.name} ({s.code})</option>)}
        </select>
      </div>
    </div>

    <form onSubmit={addChapter} className="nb-card-sm p-3 bg-white grid md:grid-cols-[1fr_1fr_auto] gap-2 items-end">
      <div>
        <div className="nb-label">Chapter Title</div>
        <input className="nb-input" placeholder="e.g. Real Numbers" required
               value={chapterForm.title}
               onChange={e=>setChapterForm({...chapterForm, title:e.target.value})}
               data-testid="lms-ch-new-title"/>
      </div>
      <div>
        <div className="nb-label">Short Description</div>
        <input className="nb-input" placeholder="optional"
               value={chapterForm.description}
               onChange={e=>setChapterForm({...chapterForm, description:e.target.value})}/>
      </div>
      <button className="nb-btn" data-testid="lms-ch-add-btn"><Plus size={16}/>Add Chapter</button>
    </form>

    <div className="space-y-2">
      <div className="flex justify-between items-center">
        <div className="text-xs mono">
          {curriculum ? `${curriculum.chapters?.length || 0} chapter(s) — ${boardCodes.join(", ")} · ${grade} · ${subjectName}`
                      : `No curriculum yet for ${primaryBoard || "?"} · ${grade} · ${subjectName}`}
        </div>
        {curriculum && (
          <button onClick={()=>setWipeStep(1)}
                  className="nb-btn nb-btn-danger px-2 py-1 text-xs"
                  data-testid="lms-cur-wipe-btn">
            <Trash2 size={12}/>Wipe Curriculum
          </button>
        )}
      </div>
      {curriculum?.chapters?.length ? (
        <div className="space-y-2">
          {curriculum.chapters.map((ch, i) => (
            <div key={ch.chapter_id} className="nb-card-sm p-3 bg-white flex items-center justify-between gap-2"
                 data-testid={`lms-ch-row-${i+1}`}>
              <div className="min-w-0">
                <div className="font-bold text-sm">{ch.order || i+1}. {ch.title}</div>
                <div className="text-xs text-slate-500 truncate">{ch.description || "—"}</div>
              </div>
              <div className="flex gap-1 shrink-0">
                <button onClick={()=>setOpenChapter(ch)} className="nb-btn nb-btn-secondary px-2 py-1 text-xs"
                        data-testid={`lms-ch-open-${i+1}`}>Course Builder</button>
                <button onClick={()=>editChapter(ch)} className="nb-btn nb-btn-ghost px-2 py-1 text-xs">
                  <Pencil size={12}/></button>
                <button onClick={()=>delChapter(ch)} className="nb-btn nb-btn-danger px-2 py-1 text-xs">
                  <Trash2 size={12}/></button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="nb-card-sm p-3 bg-white text-sm text-slate-500">No chapters yet — add one above.</div>
      )}
    </div>

    {openChapter && curriculum && (
      <CourseBuilderModal chapter={openChapter} curriculumId={curriculum.curriculum_id}
                          onClose={()=>{ setOpenChapter(null); loadCurriculum(); }}/>
    )}

    <WipeCurriculumModal step={wipeStep} onStep={setWipeStep}
                          boards={boardCodes} grade={grade} subjectName={subjectName}
                          onConfirm={performWipe}/>

    {editingChapter && (
      <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
           onClick={()=>setEditingChapter(null)}>
        <form onSubmit={saveEditChapter}
              className="nb-card bg-white p-6 max-w-lg w-full space-y-3"
              onClick={e=>e.stopPropagation()}
              data-testid="lms-ch-edit-modal">
          <div className="flex justify-between items-start">
            <div>
              <h4 className="display text-xl font-bold">Edit Chapter</h4>
              <p className="text-xs">Applies across all mapped boards: {boardCodes.join(", ")}</p>
            </div>
            <button type="button" onClick={()=>setEditingChapter(null)}
                    className="nb-btn nb-btn-secondary text-xs">✕ Close</button>
          </div>
          <div>
            <div className="nb-label">Chapter Title</div>
            <input className="nb-input" required
                   value={editingChapter.title}
                   onChange={e=>setEditingChapter({...editingChapter, title:e.target.value})}
                   data-testid="lms-ch-edit-title"/>
          </div>
          <div>
            <div className="nb-label">Short Description</div>
            <input className="nb-input" placeholder="optional"
                   value={editingChapter.description}
                   onChange={e=>setEditingChapter({...editingChapter, description:e.target.value})}
                   data-testid="lms-ch-edit-desc"/>
          </div>
          <div>
            <div className="nb-label">Order</div>
            <input type="number" className="nb-input"
                   value={editingChapter.order}
                   onChange={e=>setEditingChapter({...editingChapter, order:parseInt(e.target.value)||0})}
                   data-testid="lms-ch-edit-order"/>
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={()=>setEditingChapter(null)}
                    className="nb-btn nb-btn-ghost flex-1 justify-center">Cancel</button>
            <button className="nb-btn flex-1 justify-center" data-testid="lms-ch-edit-save">
              <Save size={14}/>Save Chapter
            </button>
          </div>
        </form>
      </div>
    )}
  </div>;
}

function WipeCurriculumModal({ step, onStep, boards, grade, subjectName, onConfirm }) {
  const [typed, setTyped] = useState("");
  useEffect(()=>{ if (step === 0) setTyped(""); }, [step]);
  if (step === 0) return null;
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4"
         onClick={()=>onStep(0)} data-testid="wipe-modal">
      <div className="nb-card p-6 bg-white max-w-md w-full space-y-3"
           onClick={e=>e.stopPropagation()}>
        {step === 1 ? (
          <>
            <div className="display text-xl font-bold text-red-700">Wipe Curriculum?</div>
            <div className="text-sm">Are you sure you want to wipe this curriculum dataset?
              <b> This action is permanent and non-reversible.</b></div>
            <div className="text-xs mono p-2 bg-red-50 rounded border border-red-200">
              Boards: {boards.join(", ")}<br/>
              Grade: {grade}<br/>
              Subject: {subjectName}<br/>
              Chapters and all lessons attached ONLY to these curriculums will also be removed.
            </div>
            <div className="flex gap-2">
              <button onClick={()=>onStep(0)} className="nb-btn nb-btn-ghost flex-1 justify-center"
                      data-testid="wipe-cancel">Cancel</button>
              <button onClick={()=>onStep(2)} className="nb-btn nb-btn-danger flex-1 justify-center"
                      data-testid="wipe-step2">I understand, continue</button>
            </div>
          </>
        ) : (
          <>
            <div className="display text-xl font-bold text-red-700">Final Confirmation</div>
            <div className="text-sm">Type <b className="mono">WIPE</b> below to confirm the deletion.</div>
            <input className="nb-input" value={typed} placeholder="WIPE"
                   onChange={e=>setTyped(e.target.value)}
                   data-testid="wipe-confirm-input"/>
            <div className="flex gap-2">
              <button onClick={()=>onStep(0)} className="nb-btn nb-btn-ghost flex-1 justify-center">Cancel</button>
              <button onClick={onConfirm} disabled={typed !== "WIPE"}
                      className="nb-btn nb-btn-danger flex-1 justify-center"
                      data-testid="wipe-confirm-final">Wipe now</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const LESSON_KIND_LABELS = {
  notes: "Study Notes (Rich Text)",
  pdf: "PDF Document",
  audio: "Audio",
  video_upload: "Video (Upload)",
  video_youtube: "Video (YouTube)",
  html: "HTML/CSS/JS Course",
};

function CourseBuilderModal({ chapter, curriculumId, onClose }) {
  const [lessons, setLessons] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null); // lesson object

  const load = useCallback(async () => {
    const r = await api.get("/global/lms/lessons", {params:{chapter_id: chapter.chapter_id}});
    setLessons(r.data);
  }, [chapter.chapter_id]);
  useEffect(()=>{ load(); }, [load]);

  const del = async (l) => {
    if (!window.confirm(`Delete lesson "${l.title}"?`)) return;
    await api.delete(`/global/lms/lessons/${l.lesson_id}`);
    toast.success("Lesson deleted"); load();
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" onClick={onClose}>
      <div className="nb-card p-6 bg-white max-w-4xl w-full max-h-[90vh] overflow-y-auto space-y-4"
           onClick={e=>e.stopPropagation()} data-testid="lms-course-builder-modal">
        <div className="flex justify-between items-start gap-3">
          <div>
            <div className="nb-label">Course Builder · Chapter {chapter.order}</div>
            <h3 className="display text-2xl font-bold">{chapter.title}</h3>
            <p className="text-xs text-slate-600">{chapter.description || "—"}</p>
          </div>
          <button onClick={onClose} className="nb-btn nb-btn-ghost">Close</button>
        </div>

        <div className="space-y-2">
          {lessons.map((l, i) => (
            <div key={l.lesson_id} className="nb-card-sm p-3 bg-slate-50 flex items-center justify-between gap-2"
                 data-testid={`lms-lesson-row-${i+1}`}>
              <div className="min-w-0">
                <span className="nb-badge bg-[#93C5FD] text-xs mr-2">{LESSON_KIND_LABELS[l.kind] || l.kind}</span>
                <span className="font-bold text-sm">{l.order}. {l.title}</span>
                {l.description && <div className="text-xs text-slate-500 truncate">{l.description}</div>}
              </div>
              <div className="flex gap-1 shrink-0">
                <button onClick={()=>{ setEditing(l); setShowForm(true); }}
                        className="nb-btn nb-btn-secondary px-2 py-1 text-xs">
                  <Pencil size={12}/></button>
                <button onClick={()=>del(l)} className="nb-btn nb-btn-danger px-2 py-1 text-xs">
                  <Trash2 size={12}/></button>
              </div>
            </div>
          ))}
          {lessons.length === 0 && !showForm && (
            <div className="nb-card-sm p-3 bg-slate-50 text-sm text-slate-500">
              No lessons yet — add one below.
            </div>
          )}
        </div>

        {!showForm ? (
          <button className="nb-btn w-full justify-center" onClick={()=>{ setEditing(null); setShowForm(true); }}
                  data-testid="lms-lesson-new-btn">
            <Plus size={16}/>Add Lesson
          </button>
        ) : (
          <LessonForm chapterId={chapter.chapter_id} curriculumId={curriculumId}
                      initial={editing}
                      onDone={()=>{ setShowForm(false); setEditing(null); load(); }}
                      onCancel={()=>{ setShowForm(false); setEditing(null); }}/>
        )}
      </div>
    </div>
  );
}

function LessonForm({ chapterId, curriculumId, initial, onDone, onCancel }) {
  const [f, setF] = useState(() => initial ? {...initial} : {
    title:"", kind:"notes", description:"", notes_html:"", html_content:"",
    storage_path:"", youtube_url:"", duration_min: null,
  });
  const [uploading, setUploading] = useState(false);

  const upload = async (file, expectedKindPrefix) => {
    if (!file) return;
    if (file.size > 200 * 1024 * 1024) return toast.error("Max 200MB");
    if (expectedKindPrefix && !file.type.startsWith(expectedKindPrefix) && file.type !== "application/pdf") {
      // Allow anyway, but warn
      toast.warning(`File type ${file.type} may not preview correctly.`);
    }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/lms/files/upload", fd, {
        headers:{"Content-Type":"multipart/form-data"}});
      setF(x => ({...x, storage_path: r.data.storage_path}));
      toast.success("Uploaded");
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    setUploading(false);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!f.title.trim()) return toast.error("Title required");
    // Kind-specific validation
    if (f.kind === "notes" && !f.notes_html.trim()) return toast.error("Notes content required");
    if (f.kind === "html" && !f.html_content.trim()) return toast.error("HTML content required");
    if ((f.kind === "pdf" || f.kind === "audio" || f.kind === "video_upload") && !f.storage_path)
      return toast.error("Please upload a file");
    if (f.kind === "video_youtube" && !f.youtube_url.trim()) return toast.error("YouTube URL required");
    try {
      if (initial?.lesson_id) {
        await api.patch(`/global/lms/lessons/${initial.lesson_id}`, {
          title: f.title, kind: f.kind, description: f.description,
          notes_html: f.notes_html, html_content: f.html_content,
          storage_path: f.storage_path, youtube_url: f.youtube_url,
          duration_min: f.duration_min || null,
        });
        toast.success("Lesson updated");
      } else {
        await api.post("/global/lms/lessons", {
          curriculum_id: curriculumId, chapter_id: chapterId,
          title: f.title, kind: f.kind, description: f.description,
          notes_html: f.notes_html, html_content: f.html_content,
          storage_path: f.storage_path, youtube_url: f.youtube_url,
          duration_min: f.duration_min || null, order: 0,
        });
        toast.success("Lesson added");
      }
      onDone();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  return (
    <form onSubmit={save} className="nb-card-sm p-4 bg-[#FDE047] space-y-3" data-testid="lms-lesson-form">
      <h4 className="display text-lg font-bold">{initial?.lesson_id ? "Edit Lesson" : "New Lesson"}</h4>
      <div className="grid md:grid-cols-2 gap-2">
        <div>
          <div className="nb-label">Title</div>
          <input className="nb-input" placeholder="Lesson title" required
                 value={f.title} onChange={e=>setF({...f,title:e.target.value})}
                 data-testid="lms-lesson-title"/>
        </div>
        <div>
          <div className="nb-label">Content Type</div>
          <select className="nb-select" value={f.kind}
                  onChange={e=>setF({...f,kind:e.target.value})}
                  data-testid="lms-lesson-kind">
            {Object.entries(LESSON_KIND_LABELS).map(([k,v])=>
              <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
      </div>
      <div>
        <div className="nb-label">Description (optional)</div>
        <input className="nb-input" placeholder="Short description"
               value={f.description||""} onChange={e=>setF({...f,description:e.target.value})}/>
      </div>

      {f.kind === "notes" && (
        <div>
          <div className="nb-label">Study Notes (basic HTML supported — &lt;b&gt; &lt;i&gt; &lt;h3&gt; &lt;ul&gt; &lt;li&gt; &lt;br&gt;)</div>
          <div className="flex gap-1 mb-1 text-xs">
            {[["<b>","</b>","B"],["<i>","</i>","I"],["<h3>","</h3>","H"],
             ["<ul><li>","</li></ul>","•"],["<br/>","","↵"]].map(([o,c,lbl])=>(
              <button key={lbl} type="button" className="nb-btn nb-btn-ghost px-2 py-0.5"
                      onClick={()=>setF(x=>({...x, notes_html: (x.notes_html||"")+o+c}))}>{lbl}</button>
            ))}
          </div>
          <textarea className="nb-textarea" rows={8}
                    placeholder="Enter study notes. HTML tags allowed."
                    value={f.notes_html||""}
                    onChange={e=>setF({...f,notes_html:e.target.value})}
                    data-testid="lms-lesson-notes"/>
          {f.notes_html && (
            <div className="nb-card-sm p-3 bg-white mt-2 text-sm prose max-w-none"
                 dangerouslySetInnerHTML={{__html: f.notes_html}}/>
          )}
        </div>
      )}

      {f.kind === "html" && (
        <div>
          <div className="nb-label">Single-file HTML (inline CSS/JS)</div>
          <textarea className="nb-textarea" rows={10}
                    placeholder="<!DOCTYPE html>..."
                    value={f.html_content||""}
                    onChange={e=>setF({...f,html_content:e.target.value})}
                    data-testid="lms-lesson-html"/>
        </div>
      )}

      {(f.kind === "pdf" || f.kind === "audio" || f.kind === "video_upload") && (
        <div>
          <div className="nb-label">Upload File
            {f.kind==="pdf" && " (.pdf)"}
            {f.kind==="audio" && " (mp3, wav, m4a)"}
            {f.kind==="video_upload" && " (mp4, webm — max 200MB)"}
          </div>
          <input type="file" className="nb-input"
                 accept={f.kind==="pdf" ? ".pdf" : f.kind==="audio" ? "audio/*" : "video/*"}
                 onChange={e=>upload(e.target.files?.[0],
                   f.kind==="audio" ? "audio/" : f.kind==="video_upload" ? "video/" : "application/")}
                 disabled={uploading}
                 data-testid="lms-lesson-file"/>
          {uploading && <div className="text-xs mt-1">Uploading…</div>}
          {f.storage_path && (
            <div className="text-xs mono mt-1 truncate text-emerald-700">
              ✓ Stored: {f.storage_path}
            </div>
          )}
        </div>
      )}

      {f.kind === "video_youtube" && (
        <div>
          <div className="nb-label">YouTube URL</div>
          <input className="nb-input" placeholder="https://www.youtube.com/watch?v=..."
                 value={f.youtube_url||""}
                 onChange={e=>setF({...f,youtube_url:e.target.value})}
                 data-testid="lms-lesson-youtube"/>
        </div>
      )}

      <div>
        <div className="nb-label">Duration (minutes, optional)</div>
        <input type="number" className="nb-input w-24" min="0"
               value={f.duration_min||""}
               onChange={e=>setF({...f, duration_min: e.target.value ? parseInt(e.target.value,10) : null})}/>
      </div>

      <div className="flex gap-2 pt-1">
        <button type="button" onClick={onCancel}
                className="nb-btn nb-btn-ghost flex-1 justify-center">Cancel</button>
        <button className="nb-btn flex-1 justify-center" disabled={uploading}
                data-testid="lms-lesson-save">
          {initial?.lesson_id ? "Save Changes" : "Add Lesson"}
        </button>
      </div>
    </form>
  );
}

const MODULE_TAB_OPTIONS = [
  "attendance","marks","report_cards","fees","expenses","payroll",
  "cash_flow","lms","time_table","schedule","donations","id_cards",
  "analytics","staff","students","admissions","events","elevate",
  "homework","communications","dashboard","reports","settings",
  "boards","curriculum","question_bank","exams_quizzes","course_builder",
  "transport","hostel","library","calendar"];

const MODULE_ROLE_OPTIONS = [
  "global_admin","school_admin","educator","student","parent",
  "accountant","facilitator","founder"];

function ModuleManagement() {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({name:"", code:"", tabs:[], roles:[]});
  const [edit, setEdit] = useState(null);
  const load = ()=> api.get("/global/modules").then(r=>{
    // Un-pack any encoded "role:xxx" entries stored under tabs.
    const decoded = r.data.map(m => {
      const raw = m.tabs || [];
      return {...m,
        tabs: raw.filter(x => !x.startsWith("role:")),
        roles: raw.filter(x => x.startsWith("role:")).map(x => x.slice(5))};
    });
    setRows(decoded);
  });
  useEffect(()=>{ load(); }, []);
  const toggleList = (obj, setter, key, v) => {
    const has = (obj[key]||[]).includes(v);
    setter({...obj, [key]: has ? obj[key].filter(x=>x!==v) : [...(obj[key]||[]), v]});
  };
  // Pack roles back onto the tabs array (with a `role:` prefix) so we don't
  // need a backend schema change to persist role visibility.
  const packTabs = (obj) => ([
    ...((obj.tabs)||[]),
    ...((obj.roles)||[]).map(r => `role:${r}`),
  ]);
  const create = async (e) => { e.preventDefault();
    try { await api.post("/global/modules", {name:f.name, code:f.code, tabs: packTabs(f)});
          toast.success("Module created");
          setF({name:"",code:"",tabs:[],roles:[]}); load();
    } catch(err){ toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const save = async () => {
    await api.patch(`/global/modules/${edit.module_id}`,
      {name: edit.name, code: edit.code, tabs: packTabs(edit)});
    toast.success("Module updated"); setEdit(null); load();
  };
  const del = async (id) => { if(!window.confirm("Delete module?")) return;
    await api.delete(`/global/modules/${id}`); load(); };
  return <div className="grid lg:grid-cols-3 gap-6" data-testid="module-mgmt">
    <form onSubmit={create} className="nb-card p-6 bg-[#86EFAC] space-y-2 h-fit">
      <h3 className="display text-xl font-bold">Module Creation Engine</h3>
      <input className="nb-input" placeholder="Module Name" required
             value={f.name} onChange={e=>setF({...f,name:e.target.value})}
             data-testid="module-new-name"/>
      <input className="nb-input" placeholder="Module Code" required
             value={f.code} onChange={e=>setF({...f,code:e.target.value})}
             data-testid="module-new-code"/>
      <div className="nb-label">Associated Tabs / Capabilities ({MODULE_TAB_OPTIONS.length})</div>
      <div className="grid grid-cols-2 gap-1 text-xs max-h-52 overflow-y-auto nb-card-sm p-2 bg-white">
        {MODULE_TAB_OPTIONS.map(t => (
          <label key={t} className="flex items-center gap-1">
            <input type="checkbox" checked={(f.tabs||[]).includes(t)}
                   onChange={()=>toggleList(f, setF, "tabs", t)}
                   data-testid={`module-tab-${t}`}/>
            {t}
          </label>
        ))}
      </div>
      <div className="nb-label">Visible to roles / consoles</div>
      <div className="grid grid-cols-2 gap-1 text-xs nb-card-sm p-2 bg-white">
        {MODULE_ROLE_OPTIONS.map(r => (
          <label key={r} className="flex items-center gap-1">
            <input type="checkbox" checked={(f.roles||[]).includes(r)}
                   onChange={()=>toggleList(f, setF, "roles", r)}
                   data-testid={`module-role-${r}`}/>
            {r.replace("_"," ")}
          </label>
        ))}
      </div>
      <button className="nb-btn w-full justify-center" data-testid="module-new-submit">
        <Plus size={16}/>Create Module</button>
    </form>
    <div className="nb-card p-0 overflow-hidden lg:col-span-2">
      <table className="nb-table">
        <thead><tr><th>Module</th><th>Code</th><th>Tabs</th><th>Roles</th><th>Actions</th></tr></thead>
        <tbody>
          {rows.map(m => <tr key={m.module_id} data-testid={`module-row-${m.code}`}>
            <td className="font-bold">{m.name}</td>
            <td className="mono">{m.code}</td>
            <td className="text-xs">{(m.tabs||[]).join(", ") || "—"}</td>
            <td className="text-xs">{(m.roles||[]).map(r=>r.replace("_"," ")).join(", ") || "—"}</td>
            <td className="space-x-1 whitespace-nowrap">
              <button onClick={()=>setEdit({...m})} className="nb-btn nb-btn-secondary px-2 py-1 text-xs"
                      data-testid={`module-edit-${m.code}`}><Pencil size={12}/></button>
              <button onClick={()=>del(m.module_id)} className="nb-btn nb-btn-danger px-2 py-1 text-xs"
                      data-testid={`module-del-${m.code}`}><Trash2 size={12}/></button>
            </td>
          </tr>)}
          {rows.length===0 && <tr><td colSpan={5} className="text-center py-4 text-slate-500">No modules yet</td></tr>}
        </tbody>
      </table>
    </div>
    {edit && (
      <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4"
           onClick={()=>setEdit(null)}>
        <div className="nb-card p-6 bg-white max-w-lg w-full space-y-2 max-h-[85vh] overflow-y-auto"
             onClick={e=>e.stopPropagation()}>
          <h3 className="display text-xl font-bold">Edit Module</h3>
          <input className="nb-input" placeholder="Name" value={edit.name}
                 onChange={e=>setEdit({...edit, name:e.target.value})}/>
          <input className="nb-input" placeholder="Code" value={edit.code}
                 onChange={e=>setEdit({...edit, code:e.target.value})}/>
          <div className="nb-label">Tabs</div>
          <div className="grid grid-cols-2 gap-1 text-xs max-h-48 overflow-y-auto nb-card-sm p-2 bg-slate-50">
            {MODULE_TAB_OPTIONS.map(t => (
              <label key={t} className="flex items-center gap-1">
                <input type="checkbox" checked={(edit.tabs||[]).includes(t)}
                       onChange={()=>toggleList(edit, setEdit, "tabs", t)}/>
                {t}
              </label>
            ))}
          </div>
          <div className="nb-label">Visible to roles</div>
          <div className="grid grid-cols-2 gap-1 text-xs nb-card-sm p-2 bg-slate-50">
            {MODULE_ROLE_OPTIONS.map(r => (
              <label key={r} className="flex items-center gap-1">
                <input type="checkbox" checked={(edit.roles||[]).includes(r)}
                       onChange={()=>toggleList(edit, setEdit, "roles", r)}/>
                {r.replace("_"," ")}
              </label>
            ))}
          </div>
          <div className="flex gap-2 pt-2">
            <button onClick={()=>setEdit(null)} className="nb-btn nb-btn-ghost flex-1 justify-center">Cancel</button>
            <button onClick={save} className="nb-btn flex-1 justify-center"
                    data-testid="module-edit-save">Save</button>
          </div>
        </div>
      </div>
    )}
  </div>;
}

function EventsModuleControls() {
  const [tpls, setTpls] = useState({parent:"", teacher:"", student:"", chief_guest:""});
  const [s, setS] = useState({visitor_broadcast_enabled:false, landing_expire_days:3,
                               per_school_storage_mb:500});
  const load = async () => {
    try { const r = await api.get("/global/events/templates");
      setTpls({parent:r.data.parent||"", teacher:r.data.teacher||"",
                student:r.data.student||"", chief_guest:r.data.chief_guest||""});
    } catch { /* silent */ }
    try { const r = await api.get("/global/events/settings"); setS(r.data); }
    catch { /* silent */ }
  };
  useEffect(()=>{ load(); }, []);
  const saveTpls = async () => {
    await api.put("/global/events/templates", tpls); toast.success("Templates saved");
  };
  const saveSettings = async () => {
    await api.put("/global/events/settings", s); toast.success("Module settings saved");
  };
  return <div className="space-y-6" data-testid="events-module-controls">
    <div className="nb-card p-6 bg-[#C4B5FD] space-y-3">
      <h3 className="display text-2xl font-bold">Event Module Controls</h3>
      <p className="text-xs">Configure per-tenant defaults for the Event Management module.
        Template variables: <span className="mono">&#123;&#123;school&#125;&#125;</span>,
        <span className="mono">&#123;&#123;event&#125;&#125;</span>,
        <span className="mono">&#123;&#123;date&#125;&#125;</span>,
        <span className="mono">&#123;&#123;manager&#125;&#125;</span>.</p>
    </div>
    <div className="grid lg:grid-cols-2 gap-4">
      <div className="nb-card p-5 bg-white space-y-3">
        <h4 className="display text-lg font-bold">Global Greeting Templates</h4>
        {["parent","teacher","student","chief_guest"].map(k => (
          <label key={k} className="block">
            <span className="nb-label uppercase">{k.replace("_"," ")}</span>
            <textarea className="nb-textarea" rows={3}
                      value={tpls[k]}
                      onChange={e=>setTpls({...tpls, [k]:e.target.value})}
                      placeholder={`(Falls back to Ponderr default for ${k})`}
                      data-testid={`events-tpl-${k}`}/>
          </label>
        ))}
        <button onClick={saveTpls} className="nb-btn w-full justify-center"
                data-testid="events-tpl-save">Save Templates</button>
      </div>
      <div className="nb-card p-5 bg-white space-y-3">
        <h4 className="display text-lg font-bold">Module Settings</h4>
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={!!s.visitor_broadcast_enabled}
                 onChange={e=>setS({...s, visitor_broadcast_enabled:e.target.checked})}
                 data-testid="events-visitor-broadcast"/>
          Automated visitor broadcast messages on event days
        </label>
        <label className="block">
          <span className="nb-label">Landing Page media auto-expire (days)</span>
          <select className="nb-select" value={s.landing_expire_days}
                  onChange={e=>setS({...s, landing_expire_days:parseInt(e.target.value)})}
                  data-testid="events-expire-days">
            <option value={3}>3 days</option>
            <option value={7}>7 days (1 week)</option>
          </select>
          <div className="text-xs text-slate-500 mt-1">
            Landing page shows only 1 summary card per event/class, auto-expires after this period.
          </div>
        </label>
        <label className="block">
          <span className="nb-label">Per-school Blob storage cap (MB)</span>
          <input type="number" className="nb-input mono" min={100} step={100}
                 value={s.per_school_storage_mb}
                 onChange={e=>setS({...s, per_school_storage_mb:parseInt(e.target.value||0)})}
                 data-testid="events-storage-cap"/>
          <div className="text-xs text-slate-500 mt-1">
            Applied when the Event Module is enabled on a tenant. (Enforced once Azure Blob is wired in Phase C.)
          </div>
        </label>
        <button onClick={saveSettings} className="nb-btn w-full justify-center"
                data-testid="events-settings-save">Save Module Settings</button>
      </div>
    </div>
  </div>;
}

/* ==================================================================
   Iter29 — Ponderr LMS Phase 3: Question Bank + Exams (Global Admin)
   ================================================================== */
const QUESTION_KINDS = [
  {v:"mcq_single", n:"MCQ — Single correct"},
  {v:"mcq_multi",  n:"MCQ — Multi-select"},
  {v:"true_false", n:"True / False"},
  {v:"short_answer", n:"Short Answer (review)"},
];
const DIFFICULTIES = ["easy","medium","hard"];

function QuestionBankManager() {
  const lmsGrades = useLmsGrades();
  const [boards, setBoards] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [board_code, setBoard] = useState("");
  const [grade, setGrade] = useState("G10");
  const [subject_id, setSubject] = useState("");
  const [kind, setKind] = useState("");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [chapters, setChapters] = useState([]);

  useEffect(()=>{
    api.get("/global/boards").then(r=>{ setBoards(r.data); if(r.data[0]) setBoard(x=>x||r.data[0].code); });
    api.get("/global/lms/subjects").then(r=>{ setSubjects(r.data); if(r.data[0]) setSubject(x=>x||r.data[0].subject_id); });
  }, []);

  // Preload chapters for current board+grade+subject
  useEffect(()=>{
    if (!board_code || !grade || !subject_id) { setChapters([]); return; }
    api.get("/global/lms/curriculums", {params:{board_code, grade, subject_id}})
       .then(r => setChapters(r.data?.[0]?.chapters || []));
  }, [board_code, grade, subject_id]);

  const load = useCallback(async () => {
    const params = {}; if (board_code) params.board_code = board_code;
    if (grade) params.grade = grade; if (subject_id) params.subject_id = subject_id;
    if (kind) params.kind = kind; if (q) params.q = q;
    const r = await api.get("/global/lms/questions", { params });
    setRows(r.data);
  }, [board_code, grade, subject_id, kind, q]);
  useEffect(()=>{ load(); }, [load]);

  const del = async (qid) => {
    if (!window.confirm("Delete question? Removes from any exam that uses it.")) return;
    await api.delete(`/global/lms/questions/${qid}`); toast.success("Deleted"); load();
  };

  return <div className="nb-card p-6 bg-[#FDE047] space-y-4" data-testid="lms-qbank">
    <div>
      <h3 className="display text-2xl font-bold">Question Bank</h3>
      <p className="text-xs">Every question authored here is reusable across chapter quizzes and end-of-subject exams. Auto-graded MCQ / True-False, plus reviewable short answers.</p>
    </div>
    <div className="grid md:grid-cols-5 gap-2">
      <select className="nb-select" value={board_code} onChange={e=>setBoard(e.target.value)} data-testid="qbank-board">
        <option value="">Any Board</option>
        {boards.map(b => <option key={b.code} value={b.code}>{b.code}</option>)}
      </select>
      <select className="nb-select" value={grade} onChange={e=>setGrade(e.target.value)} data-testid="qbank-grade">
        <option value="">Any Grade</option>
        {lmsGrades.map(g => <option key={g} value={g}>{g}</option>)}
      </select>
      <select className="nb-select" value={subject_id} onChange={e=>setSubject(e.target.value)} data-testid="qbank-subject">
        <option value="">Any Subject</option>
        {subjects.map(s => <option key={s.subject_id} value={s.subject_id}>{s.name}</option>)}
      </select>
      <select className="nb-select" value={kind} onChange={e=>setKind(e.target.value)}>
        <option value="">Any Kind</option>
        {QUESTION_KINDS.map(k => <option key={k.v} value={k.v}>{k.n}</option>)}
      </select>
      <input className="nb-input" placeholder="Search…" value={q} onChange={e=>setQ(e.target.value)}/>
    </div>

    <button onClick={()=>{ setEditing(null); setShowForm(true); }}
            className="nb-btn" data-testid="qbank-new-btn">
      <Plus size={16}/>Add Question
    </button>

    {showForm && (
      <QuestionForm chapters={chapters} defaults={{board_code, grade, subject_id}}
                    initial={editing}
                    onDone={()=>{ setShowForm(false); setEditing(null); load(); }}
                    onCancel={()=>{ setShowForm(false); setEditing(null); }}/>
    )}

    <div className="nb-card-sm p-0 bg-white overflow-x-auto">
      <table className="nb-table"><thead><tr>
        <th>Kind</th><th>Board / Grade / Subject</th><th>Question</th><th>Marks</th><th></th>
      </tr></thead><tbody>
      {rows.map(r => (
        <tr key={r.question_id} data-testid={`qbank-row-${r.question_id}`}>
          <td><span className="nb-badge bg-[#93C5FD] text-xs">
            {QUESTION_KINDS.find(k=>k.v===r.kind)?.n || r.kind}
          </span></td>
          <td className="text-xs mono">{r.board_code} · {r.grade} · {subjects.find(s=>s.subject_id===r.subject_id)?.code}</td>
          <td className="text-xs max-w-md truncate" dangerouslySetInnerHTML={{__html: r.question_html}}/>
          <td className="text-xs">{r.marks}</td>
          <td className="whitespace-nowrap">
            <button onClick={()=>{ setEditing(r); setShowForm(true); }}
                    className="nb-btn nb-btn-secondary px-2 py-1 text-xs">
              <Pencil size={12}/></button>
            <button onClick={()=>del(r.question_id)}
                    className="nb-btn nb-btn-danger px-2 py-1 text-xs ml-1">
              <Trash2 size={12}/></button>
          </td>
        </tr>
      ))}
      {rows.length===0 && <tr><td colSpan={5} className="text-center py-4 text-slate-500">No questions match your filters</td></tr>}
      </tbody></table>
    </div>
  </div>;
}

function QuestionForm({ chapters, defaults, initial, onDone, onCancel }) {
  const lmsGrades = useLmsGrades();
  const [f, setF] = useState(() => initial ? {...initial} : {
    kind: "mcq_single", board_code: defaults.board_code || "",
    grade: defaults.grade || "G10", subject_id: defaults.subject_id || "",
    chapter_id: "", difficulty: "medium",
    question_html: "", options: ["", ""], correct_indices: [],
    correct_bool: false, model_answer: "", marks: 1, tags: [], explanation: "",
  });
  const [boards, setBoards] = useState([]);
  const [subjects, setSubjects] = useState([]);
  useEffect(()=>{
    api.get("/global/boards").then(r=>setBoards(r.data));
    api.get("/global/lms/subjects").then(r=>setSubjects(r.data));
  }, []);

  const setOpt = (i, v) => { const opts = [...(f.options||[])]; opts[i]=v; setF({...f, options: opts}); };
  const addOpt = () => setF({...f, options: [...(f.options||[]), ""]});
  const rmOpt = (i) => {
    const opts = f.options.filter((_,j)=>j!==i);
    const corr = (f.correct_indices||[]).filter(x=>x!==i).map(x=>x>i?x-1:x);
    setF({...f, options: opts, correct_indices: corr});
  };
  const toggleCorrect = (i) => {
    if (f.kind === "mcq_single") { setF({...f, correct_indices:[i]}); return; }
    const set = new Set(f.correct_indices||[]);
    if (set.has(i)) set.delete(i); else set.add(i);
    setF({...f, correct_indices: [...set]});
  };

  const save = async (e) => {
    e.preventDefault();
    if (!f.question_html.trim()) return toast.error("Question required");
    if ((f.kind==="mcq_single" || f.kind==="mcq_multi")) {
      if ((f.options||[]).some(o=>!o.trim())) return toast.error("All options must be filled");
      if (!f.correct_indices?.length) return toast.error("Mark at least one correct answer");
    }
    try {
      const payload = {
        kind: f.kind, board_code: f.board_code, grade: f.grade,
        subject_id: f.subject_id, chapter_id: f.chapter_id || null,
        difficulty: f.difficulty, question_html: f.question_html,
        options: f.options || [], correct_indices: f.correct_indices || [],
        correct_bool: f.kind === "true_false" ? !!f.correct_bool : null,
        model_answer: f.model_answer || "", marks: f.marks || 1,
        tags: f.tags || [], explanation: f.explanation || "",
      };
      if (initial?.question_id) {
        await api.patch(`/global/lms/questions/${initial.question_id}`, payload);
        toast.success("Question updated");
      } else {
        await api.post("/global/lms/questions", payload); toast.success("Question added");
      }
      onDone();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  return (
    <form onSubmit={save} className="nb-card-sm p-4 bg-white space-y-3" data-testid="qbank-form">
      <div className="grid md:grid-cols-4 gap-2">
        <select className="nb-select" value={f.kind}
                onChange={e=>setF({...f, kind:e.target.value,
                  correct_indices: e.target.value==="mcq_single" ? f.correct_indices?.slice(0,1) : f.correct_indices})}
                data-testid="qform-kind">
          {QUESTION_KINDS.map(k => <option key={k.v} value={k.v}>{k.n}</option>)}
        </select>
        <select className="nb-select" value={f.board_code} onChange={e=>setF({...f, board_code:e.target.value})} required>
          <option value="">Board…</option>
          {boards.map(b => <option key={b.code} value={b.code}>{b.code}</option>)}
        </select>
        <select className="nb-select" value={f.grade} onChange={e=>setF({...f, grade:e.target.value})} required>
          {lmsGrades.map(g => <option key={g} value={g}>{g}</option>)}
        </select>
        <select className="nb-select" value={f.subject_id} onChange={e=>setF({...f, subject_id:e.target.value})} required>
          <option value="">Subject…</option>
          {subjects.map(s => <option key={s.subject_id} value={s.subject_id}>{s.name}</option>)}
        </select>
      </div>
      <div className="grid md:grid-cols-4 gap-2">
        <select className="nb-select" value={f.chapter_id || ""}
                onChange={e=>setF({...f, chapter_id: e.target.value})}>
          <option value="">Chapter (optional)</option>
          {chapters.map(c => <option key={c.chapter_id} value={c.chapter_id}>{c.order}. {c.title}</option>)}
        </select>
        <select className="nb-select" value={f.difficulty}
                onChange={e=>setF({...f, difficulty:e.target.value})}>
          {DIFFICULTIES.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
        <input type="number" min="1" className="nb-input" placeholder="Marks" value={f.marks}
               onChange={e=>setF({...f, marks: parseInt(e.target.value,10) || 1})}/>
      </div>
      <textarea className="nb-textarea" rows={3} placeholder="Question text (HTML allowed)"
                value={f.question_html}
                onChange={e=>setF({...f, question_html:e.target.value})}
                data-testid="qform-question"/>

      {(f.kind==="mcq_single" || f.kind==="mcq_multi") && (
        <div className="space-y-1">
          <div className="nb-label">Options — click ✓ to mark correct</div>
          {(f.options||[]).map((o, i) => (
            <div key={i} className="flex gap-2 items-center">
              <button type="button" onClick={()=>toggleCorrect(i)}
                      className={`nb-btn px-2 py-1 text-xs ${f.correct_indices?.includes(i) ? "bg-[#86EFAC]" : "nb-btn-ghost"}`}
                      data-testid={`qform-opt-correct-${i}`}>
                {f.correct_indices?.includes(i) ? "✓" : "○"}
              </button>
              <input className="nb-input flex-1" value={o}
                     placeholder={`Option ${i+1}`}
                     onChange={e=>setOpt(i, e.target.value)}
                     data-testid={`qform-opt-${i}`}/>
              <button type="button" onClick={()=>rmOpt(i)}
                      className="nb-btn nb-btn-danger px-2 py-1 text-xs">
                <Trash2 size={12}/></button>
            </div>
          ))}
          <button type="button" onClick={addOpt} className="nb-btn nb-btn-ghost text-xs">
            <Plus size={12}/>Add Option
          </button>
        </div>
      )}

      {f.kind==="true_false" && (
        <div className="flex gap-2 items-center">
          <div className="nb-label">Correct Answer:</div>
          <button type="button" onClick={()=>setF({...f, correct_bool:true})}
                  className={`nb-btn ${f.correct_bool ? "bg-[#86EFAC]" : "nb-btn-ghost"}`}
                  data-testid="qform-tf-true">True</button>
          <button type="button" onClick={()=>setF({...f, correct_bool:false})}
                  className={`nb-btn ${!f.correct_bool ? "bg-[#FCA5A5]" : "nb-btn-ghost"}`}
                  data-testid="qform-tf-false">False</button>
        </div>
      )}

      {f.kind==="short_answer" && (
        <div>
          <div className="nb-label">Model Answer (rubric for reviewer)</div>
          <textarea className="nb-textarea" rows={2} value={f.model_answer||""}
                    onChange={e=>setF({...f, model_answer:e.target.value})}/>
        </div>
      )}

      <div>
        <div className="nb-label">Explanation (optional — shown after submission)</div>
        <textarea className="nb-textarea" rows={2} value={f.explanation||""}
                  onChange={e=>setF({...f, explanation:e.target.value})}/>
      </div>

      <div className="flex gap-2">
        <button type="button" onClick={onCancel} className="nb-btn nb-btn-ghost flex-1 justify-center">Cancel</button>
        <button className="nb-btn flex-1 justify-center" data-testid="qform-save">
          {initial?.question_id ? "Save" : "Add Question"}
        </button>
      </div>
    </form>
  );
}

function ExamsManager() {
  const lmsGrades = useLmsGrades();
  const [boards, setBoards] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [board_code, setBoard] = useState("");
  const [grade, setGrade] = useState("G10");
  const [subject_id, setSubject] = useState("");
  const [rows, setRows] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showRandom, setShowRandom] = useState(false);

  useEffect(()=>{
    api.get("/global/boards").then(r=>{ setBoards(r.data); if(r.data[0]) setBoard(x=>x||r.data[0].code); });
    api.get("/global/lms/subjects").then(r=>{ setSubjects(r.data); if(r.data[0]) setSubject(x=>x||r.data[0].subject_id); });
  }, []);
  useEffect(()=>{
    if (!board_code || !grade || !subject_id) return;
    api.get("/global/lms/curriculums", {params:{board_code, grade, subject_id}})
       .then(r => setChapters(r.data?.[0]?.chapters || []));
  }, [board_code, grade, subject_id]);

  const load = useCallback(async () => {
    const params = {}; if (board_code) params.board_code = board_code;
    if (grade) params.grade = grade; if (subject_id) params.subject_id = subject_id;
    const r = await api.get("/global/lms/exams", { params });
    setRows(r.data);
  }, [board_code, grade, subject_id]);
  useEffect(()=>{ load(); }, [load]);

  const publish = async (e) => {
    try {
      await api.post(`/global/lms/exams/${e.exam_id}/publish`, { is_published: !e.is_published });
      toast.success(e.is_published ? "Unpublished" : "Published");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const del = async (e) => {
    if (!window.confirm("Delete exam and all attempts?")) return;
    await api.delete(`/global/lms/exams/${e.exam_id}`); toast.success("Deleted"); load();
  };

  return <div className="nb-card p-6 bg-[#93C5FD] space-y-4" data-testid="lms-exams">
    <div>
      <h3 className="display text-2xl font-bold">Exams &amp; Quizzes</h3>
      <p className="text-xs">Attach an exam to a chapter (chapter quiz) or a subject (end-of-subject exam). Auto-generate from the Question Bank or hand-pick.</p>
    </div>
    <div className="grid md:grid-cols-3 gap-2">
      <select className="nb-select" value={board_code} onChange={e=>setBoard(e.target.value)}>
        {boards.map(b => <option key={b.code} value={b.code}>{b.code}</option>)}
      </select>
      <select className="nb-select" value={grade} onChange={e=>setGrade(e.target.value)}>
        {lmsGrades.map(g => <option key={g} value={g}>{g}</option>)}
      </select>
      <select className="nb-select" value={subject_id} onChange={e=>setSubject(e.target.value)}>
        {subjects.map(s => <option key={s.subject_id} value={s.subject_id}>{s.name}</option>)}
      </select>
    </div>
    <div className="flex gap-2">
      <button onClick={()=>{ setEditing(null); setShowForm(true); }} className="nb-btn"
              data-testid="exam-new-btn"><Plus size={16}/>New Exam</button>
      <button onClick={()=>setShowRandom(true)} className="nb-btn nb-btn-secondary"
              data-testid="exam-random-btn"><Plus size={16}/>Random Quiz</button>
    </div>

    {showForm && (
      <ExamForm chapters={chapters} defaults={{board_code, grade, subject_id}}
                initial={editing}
                onDone={()=>{ setShowForm(false); setEditing(null); load(); }}
                onCancel={()=>{ setShowForm(false); setEditing(null); }}/>
    )}
    {showRandom && (
      <RandomExamForm chapters={chapters} defaults={{board_code, grade, subject_id}}
                      onDone={()=>{ setShowRandom(false); load(); }}
                      onCancel={()=>setShowRandom(false)}/>
    )}

    <div className="space-y-2">
      {rows.length === 0 && <div className="nb-card-sm p-3 bg-white text-xs text-slate-500">No exams for this filter.</div>}
      {rows.map(ex => (
        <div key={ex.exam_id} className="nb-card-sm p-3 bg-white flex justify-between items-center gap-2"
             data-testid={`exam-row-${ex.exam_id}`}>
          <div className="min-w-0">
            <div className="flex gap-2 items-center">
              <span className="nb-badge text-xs bg-[#C4B5FD]">{ex.scope}</span>
              {ex.is_published
                ? <span className="nb-badge text-xs bg-[#86EFAC]">published</span>
                : <span className="nb-badge text-xs bg-slate-200">draft</span>}
              <span className="font-bold">{ex.title}</span>
            </div>
            <div className="text-xs mt-1 mono">
              {ex.question_ids?.length || 0} q · pass {ex.passing_pct}% · attempts {ex.max_attempts || "∞"}
              {ex.duration_min ? ` · ${ex.duration_min}m` : ""}
            </div>
          </div>
          <div className="flex gap-1 shrink-0">
            <button onClick={()=>publish(ex)} className="nb-btn nb-btn-secondary px-2 py-1 text-xs"
                    data-testid={`exam-pub-${ex.exam_id}`}>
              {ex.is_published ? "Unpublish" : "Publish"}
            </button>
            <button onClick={()=>{ setEditing(ex); setShowForm(true); }}
                    className="nb-btn nb-btn-ghost px-2 py-1 text-xs"><Pencil size={12}/></button>
            <button onClick={()=>del(ex)} className="nb-btn nb-btn-danger px-2 py-1 text-xs">
              <Trash2 size={12}/></button>
          </div>
        </div>
      ))}
    </div>
  </div>;
}

function ExamForm({ chapters, defaults, initial, onDone, onCancel }) {
  const [f, setF] = useState(() => initial ? {...initial} : {
    scope: "chapter", board_code: defaults.board_code, grade: defaults.grade,
    subject_id: defaults.subject_id, chapter_id: chapters[0]?.chapter_id || "",
    title: "", description: "", passing_pct: 60,
    max_attempts: null, duration_min: null, question_ids: [],
  });
  const [pool, setPool] = useState([]);

  useEffect(()=>{
    const params = {board_code: f.board_code, grade: f.grade, subject_id: f.subject_id};
    if (f.scope==="chapter" && f.chapter_id) params.chapter_id = f.chapter_id;
    api.get("/global/lms/questions", {params}).then(r => setPool(r.data));
  }, [f.board_code, f.grade, f.subject_id, f.scope, f.chapter_id]);

  const toggleQ = (qid) => {
    const set = new Set(f.question_ids || []);
    if (set.has(qid)) set.delete(qid); else set.add(qid);
    setF({...f, question_ids: [...set]});
  };

  const save = async (e) => {
    e.preventDefault();
    if (!f.title.trim()) return toast.error("Title required");
    if (f.scope==="chapter" && !f.chapter_id) return toast.error("Chapter required");
    try {
      if (initial?.exam_id) {
        await api.patch(`/global/lms/exams/${initial.exam_id}`, {
          title: f.title, description: f.description || "",
          passing_pct: f.passing_pct || 60,
          max_attempts: f.max_attempts || null,
          duration_min: f.duration_min || null,
          question_ids: f.question_ids || [],
        });
        toast.success("Exam updated");
      } else {
        await api.post("/global/lms/exams", {
          scope: f.scope, board_code: f.board_code, grade: f.grade,
          subject_id: f.subject_id, chapter_id: f.scope==="chapter" ? f.chapter_id : null,
          title: f.title, description: f.description || "",
          passing_pct: f.passing_pct || 60,
          max_attempts: f.max_attempts || null,
          duration_min: f.duration_min || null,
          question_ids: f.question_ids || [], is_published: false,
        });
        toast.success("Exam created (unpublished)");
      }
      onDone();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  return <form onSubmit={save} className="nb-card-sm p-4 bg-white space-y-3" data-testid="exam-form">
    <div className="grid md:grid-cols-2 gap-2">
      <input className="nb-input" placeholder="Exam title" required value={f.title}
             onChange={e=>setF({...f, title:e.target.value})} data-testid="exam-title"/>
      {!initial?.exam_id && (
        <select className="nb-select" value={f.scope} onChange={e=>setF({...f, scope:e.target.value})}
                data-testid="exam-scope">
          <option value="chapter">Chapter Quiz</option>
          <option value="subject">End-of-subject Exam</option>
        </select>
      )}
      {f.scope==="chapter" && !initial?.exam_id && (
        <select className="nb-select" value={f.chapter_id}
                onChange={e=>setF({...f, chapter_id:e.target.value})} required
                data-testid="exam-chapter">
          <option value="">Pick chapter…</option>
          {chapters.map(c => <option key={c.chapter_id} value={c.chapter_id}>{c.order}. {c.title}</option>)}
        </select>
      )}
      <input type="number" className="nb-input" placeholder="Passing %" min="0" max="100"
             value={f.passing_pct||60} onChange={e=>setF({...f, passing_pct: parseInt(e.target.value,10)||60})}/>
      <input type="number" className="nb-input" placeholder="Max attempts (empty = ∞)" min="1"
             value={f.max_attempts||""} onChange={e=>setF({...f, max_attempts: e.target.value ? parseInt(e.target.value,10) : null})}
             data-testid="exam-max-attempts"/>
      <input type="number" className="nb-input" placeholder="Duration (min, optional)" min="1"
             value={f.duration_min||""} onChange={e=>setF({...f, duration_min: e.target.value ? parseInt(e.target.value,10) : null})}/>
    </div>
    <textarea className="nb-textarea" rows={2} placeholder="Description (optional)"
              value={f.description||""} onChange={e=>setF({...f, description:e.target.value})}/>
    <div>
      <div className="nb-label">Questions ({(f.question_ids||[]).length} selected — pool {pool.length})</div>
      <div className="nb-card-sm bg-slate-50 max-h-64 overflow-y-auto p-2 space-y-1">
        {pool.length === 0 && <div className="text-xs text-slate-500 p-2">No questions in this scope. Add some from the Question Bank first.</div>}
        {pool.map(q => (
          <label key={q.question_id} className="flex items-start gap-2 text-xs p-1 hover:bg-white cursor-pointer">
            <input type="checkbox" checked={(f.question_ids||[]).includes(q.question_id)}
                   onChange={()=>toggleQ(q.question_id)}
                   data-testid={`exam-pick-${q.question_id}`}/>
            <span className="nb-badge text-xs bg-[#FDE047] shrink-0">
              {QUESTION_KINDS.find(k=>k.v===q.kind)?.n?.split(" ")[0] || q.kind}
            </span>
            <span className="flex-1 min-w-0 truncate" dangerouslySetInnerHTML={{__html: q.question_html}}/>
            <span className="mono">{q.marks}m</span>
          </label>
        ))}
      </div>
    </div>
    <div className="flex gap-2">
      <button type="button" onClick={onCancel} className="nb-btn nb-btn-ghost flex-1 justify-center">Cancel</button>
      <button className="nb-btn flex-1 justify-center" data-testid="exam-save">Save Exam</button>
    </div>
  </form>;
}

function RandomExamForm({ chapters, defaults, onDone, onCancel }) {
  const [f, setF] = useState({
    scope: "chapter", chapter_id: chapters[0]?.chapter_id || "",
    title: "", count: 10, passing_pct: 60, max_attempts: null,
    duration_min: 15, kinds: ["mcq_single"], difficulty: "",
  });
  const toggleKind = (k) => {
    const set = new Set(f.kinds); if (set.has(k)) set.delete(k); else set.add(k);
    setF({...f, kinds: [...set]});
  };
  const submit = async (e) => {
    e.preventDefault();
    if (!f.title.trim()) return toast.error("Title required");
    if (!f.kinds.length) return toast.error("Pick at least one question kind");
    try {
      const r = await api.post("/global/lms/exams/random", {
        scope: f.scope, board_code: defaults.board_code, grade: defaults.grade,
        subject_id: defaults.subject_id,
        chapter_id: f.scope==="chapter" ? f.chapter_id : null,
        title: f.title, passing_pct: f.passing_pct || 60,
        max_attempts: f.max_attempts || null,
        duration_min: f.duration_min || null,
        count: f.count || 10, kinds: f.kinds,
        difficulty: f.difficulty || null,
      });
      toast.success(`Generated exam with ${r.data.count} questions`); onDone();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  return <form onSubmit={submit} className="nb-card-sm p-4 bg-[#FDE047] space-y-3" data-testid="exam-random-form">
    <h4 className="display text-lg font-bold">Auto-generate from Question Bank</h4>
    <div className="grid md:grid-cols-2 gap-2">
      <input className="nb-input" placeholder="Exam title" required value={f.title}
             onChange={e=>setF({...f, title:e.target.value})}/>
      <select className="nb-select" value={f.scope} onChange={e=>setF({...f, scope:e.target.value})}>
        <option value="chapter">Chapter Quiz</option>
        <option value="subject">End-of-subject Exam</option>
      </select>
      {f.scope==="chapter" && (
        <select className="nb-select" value={f.chapter_id}
                onChange={e=>setF({...f, chapter_id:e.target.value})} required>
          <option value="">Pick chapter…</option>
          {chapters.map(c => <option key={c.chapter_id} value={c.chapter_id}>{c.order}. {c.title}</option>)}
        </select>
      )}
      <input type="number" className="nb-input" min="1" placeholder="Count" value={f.count}
             onChange={e=>setF({...f, count: parseInt(e.target.value,10)||10})}/>
      <input type="number" className="nb-input" min="0" max="100" placeholder="Passing %"
             value={f.passing_pct} onChange={e=>setF({...f, passing_pct: parseInt(e.target.value,10)||60})}/>
      <input type="number" className="nb-input" min="1" placeholder="Max attempts (∞)"
             value={f.max_attempts||""} onChange={e=>setF({...f, max_attempts: e.target.value ? parseInt(e.target.value,10) : null})}/>
      <select className="nb-select" value={f.difficulty} onChange={e=>setF({...f, difficulty:e.target.value})}>
        <option value="">Any difficulty</option>
        {DIFFICULTIES.map(d => <option key={d} value={d}>{d}</option>)}
      </select>
    </div>
    <div className="flex gap-2 flex-wrap">
      {QUESTION_KINDS.map(k => (
        <button key={k.v} type="button" onClick={()=>toggleKind(k.v)}
                className={`nb-btn px-2 py-1 text-xs ${f.kinds.includes(k.v) ? "bg-[#86EFAC]" : "nb-btn-ghost"}`}>
          {k.n}
        </button>
      ))}
    </div>
    <div className="flex gap-2">
      <button type="button" onClick={onCancel} className="nb-btn nb-btn-ghost flex-1 justify-center">Cancel</button>
      <button className="nb-btn flex-1 justify-center" data-testid="exam-random-save">Generate</button>
    </div>
  </form>;
}

// ------------------------------------------------------------------
// Iter48 — Grades Manager (static 15 master codes) + Course Builder Tools
// (CSV/JSON bulk loaders + Gemini AI generator). Global Admin only.
// ------------------------------------------------------------------
function GradesManager() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/global/master-grades").then(r => setRows(r.data)).catch(()=>{}); }, []);
  return (
    <div className="nb-card p-6 bg-[#FDE68A] space-y-3" data-testid="lms-grades-mgr">
      <div>
        <h3 className="display text-2xl font-bold">Grades Manager</h3>
        <p className="text-xs">Standard K-12 master grade taxonomy. Schools map their legacy grade names to these codes in
          <strong> Settings → Available Grades</strong> — never rewriting live student rows.</p>
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
        {rows.map(g => (
          <div key={g.code} className="nb-card-sm p-2 bg-white flex justify-between items-center"
               data-testid={`master-grade-${g.code}`}>
            <span className="text-xs font-bold">{g.name}</span>
            <span className="mono text-xs bg-slate-100 px-2 py-0.5 rounded">{g.code}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function CourseBuilderTools() {
  const lmsGrades = useLmsGrades();
  const [aiOpen, setAiOpen] = useState(false);
  // Iter48+ — Pull Boards / Grades / Subjects from Ponderr LMS tabs.
  const [lmsBoards, setLmsBoards] = useState([]);
  const [lmsSubjects, setLmsSubjects] = useState([]);
  const [aiBoardCodes, setAiBoardCodes] = useState([]);
  const [aiGrade, setAiGrade] = useState("G09");
  const [aiSubjectId, setAiSubjectId] = useState("");
  const [aiChapters, setAiChapters] = useState([]);
  const [aiChapterId, setAiChapterId] = useState("");
  const [aiBoardsMenuOpen, setAiBoardsMenuOpen] = useState(false);
  // Iter48c — 3 independent generation surfaces + source material + push flow
  const [srcFile, setSrcFile] = useState(null);
  const [srcText, setSrcText] = useState("");
  const [srcLoading, setSrcLoading] = useState(false);
  const [lessonOut, setLessonOut] = useState(null);      // {lessons:[...]}
  const [quizOut, setQuizOut] = useState(null);          // {questions:[...]}
  const [examOut, setExamOut] = useState(null);          // {title, sections:[...]}
  const [quizCount, setQuizCount] = useState(5);
  const [lessonCount, setLessonCount] = useState(2);
  const [loading, setLoading] = useState("");            // "lessons"|"quiz"|"exam"|""
  const [pushing, setPushing] = useState("");

  useEffect(()=>{
    api.get("/global/boards").then(r=>{
      setLmsBoards(r.data);
      setAiBoardCodes(prev => prev.length ? prev : (r.data[0] ? [r.data[0].code] : []));
    }).catch(()=>{});
    api.get("/global/lms/subjects").then(r=>{
      setLmsSubjects(r.data);
      setAiSubjectId(prev => prev || (r.data[0]?.subject_id || ""));
    }).catch(()=>{});
  }, []);

  // Load chapters for the primary (first) selected board when B/G/S set.
  useEffect(()=>{
    const primary = aiBoardCodes[0];
    if (!primary || !aiGrade || !aiSubjectId) { setAiChapters([]); setAiChapterId(""); return; }
    (async () => {
      try {
        const list = await api.get("/global/lms/curriculums",
          { params: { board_code: primary, grade: aiGrade, subject_id: aiSubjectId }});
        if (list.data?.[0]) {
          const det = await api.get(`/global/lms/curriculums/${list.data[0].curriculum_id}`);
          setAiChapters(det.data.chapters || []);
        } else { setAiChapters([]); }
      } catch { setAiChapters([]); }
      setAiChapterId("");
    })();
  }, [aiBoardCodes, aiGrade, aiSubjectId]);

  const toggleAiBoard = (code) => {
    setAiBoardCodes(prev => {
      const set = new Set(prev);
      if (set.has(code)) set.delete(code); else set.add(code);
      const next = [...set];
      return next.length ? next : [code];
    });
  };
  const downloadTemplate = async (kind) => {
    try {
      const res = await api.get(`/lms/bulk/${kind}/template`, {responseType: "blob"});
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url;
      a.download = kind === "chapters" ? "lms_chapters_template.csv" : "lms_quiz_template.json";
      a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error("Download failed: " + (e?.response?.data?.detail || e.message)); }
  };
  const uploadChapters = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post("/lms/bulk/chapters/upload", fd);
      const d = r.data;
      toast.success(`Chapters: +${d.created_chapters} created, ${d.updated_chapters} updated, +${d.created_lessons} lessons`);
      if (d.errors?.length) toast.error(`${d.errors.length} row error(s) — check console`);
      e.target.value = ""; // reset for re-upload
    } catch (err) { toast.error("Upload failed: " + (err?.response?.data?.detail || err.message)); }
  };
  const uploadQuiz = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    try {
      const text = await file.text();
      const payload = JSON.parse(text);
      const r = await api.post("/lms/bulk/quiz/upload", payload);
      const d = r.data;
      toast.success(`Quiz: +${d.created} created, ${d.updated} updated`);
      if (d.errors?.length) toast.error(`${d.errors.length} question error(s) — check console`);
      e.target.value = "";
    } catch (err) {
      toast.error("Upload failed: " + (err?.response?.data?.detail || err.message));
    }
  };
  const exportCourse = async () => {
    const cid = window.prompt("Curriculum ID to export (find in Curriculum Manager below):");
    if (!cid) return;
    try {
      const res = await api.get(`/lms/bulk/export/${cid}`, {responseType: "blob"});
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url;
      a.download = `course_${cid}.json`; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error("Export failed: " + (e?.response?.data?.detail || e.message)); }
  };
  const openAI = () => {
    setLessonOut(null); setQuizOut(null); setExamOut(null);
    setSrcFile(null); setSrcText("");
    setAiOpen(true);
  };
  const uploadSource = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setSrcFile(file); setSrcLoading(true); setSrcText("");
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/lms/ai/extract-source", fd);
      setSrcText(r.data.text || "");
      toast.success(`Extracted ${r.data.chars} chars from ${r.data.filename}`);
    } catch (err) {
      toast.error("Extract failed: " + (err?.response?.data?.detail || err.message));
      setSrcFile(null);
    } finally { setSrcLoading(false); }
  };
  const buildContext = () => {
    const subj = lmsSubjects.find(s=>s.subject_id === aiSubjectId);
    const ch   = aiChapters.find(c=>c.chapter_id === aiChapterId);
    return {
      boards: aiBoardCodes.join(", "),
      board: aiBoardCodes[0] || "",
      grade: aiGrade,
      subject: subj ? subj.name : "",
      subject_code: subj?.code || "",
      chapter: ch ? ch.title : "",
    };
  };
  const runGen = async (surface, count, out_setter) => {
    if (!aiChapterId) return toast.error("Select a chapter before generating");
    setLoading(surface); out_setter(null);
    try {
      // 1) Enqueue a background job (returns immediately).
      const r = await api.post("/lms/ai/generate-content", {
        surface, context: buildContext(),
        count: Number(count)||5, source_text: srcText || "",
      });
      const jobId = r.data.job_id;
      if (!jobId) throw new Error("No job_id in response");
      // 2) Poll every 3 s (up to ~3 min) until the job finishes.
      // Poll until finished (max ~3 min).
      const t0 = Date.now();
      let last = { status: "pending" };
      while (last.status === "pending" || last.status === "running") {
        await new Promise(res => setTimeout(res, 3000));
        const s = await api.get(`/lms/ai/job/${jobId}`);
        last = s.data;
        if (Date.now() - t0 > 200_000) { last = {status:"failed", error:"Polling timeout after 200 s"}; break; }
      }
      if (last.status === "failed") throw new Error(last.error || "Job failed");
      out_setter(last.payload);
      toast.success(`AI generated ${surface.replace("_"," ")}`);
    } catch (e) {
      toast.error("AI: " + (e?.response?.data?.detail || e.message));
    } finally { setLoading(""); }
  };
  const pushLessons = async () => {
    if (!aiChapterId) return toast.error("Chapter required");
    if (!lessonOut?.lessons?.length) return toast.error("Nothing to push — generate lessons first");
    setPushing("lessons");
    try {
      const r = await api.post("/lms/ai/push-lessons", {
        chapter_id: aiChapterId, lessons: lessonOut.lessons,
      });
      toast.success(`Pushed ${r.data.created} lesson(s) to chapter (${r.data.curriculums_referencing} board(s))`);
      setLessonOut(null);
    } catch (e) {
      toast.error("Push failed: " + (e?.response?.data?.detail || e.message));
    } finally { setPushing(""); }
  };
  const pushQuiz = async () => {
    if (!aiChapterId) return toast.error("Chapter required");
    if (!quizOut?.questions?.length) return toast.error("Nothing to push — generate quiz first");
    setPushing("quiz");
    try {
      const r = await api.post("/lms/ai/push-quiz", {
        board_codes: aiBoardCodes, grade: aiGrade,
        subject_id: aiSubjectId, chapter_id: aiChapterId,
        questions: quizOut.questions,
      });
      toast.success(`Quiz pushed: +${r.data.created} new, ${r.data.updated} updated`);
      setQuizOut(null);
    } catch (e) {
      toast.error("Push failed: " + (e?.response?.data?.detail || e.message));
    } finally { setPushing(""); }
  };
  // Update fields on a specific lesson block (title, notes, duration, media)
  const patchLesson = (idx, patch) => {
    setLessonOut(prev => ({
      ...prev,
      lessons: prev.lessons.map((l, i) => i === idx ? {...l, ...patch} : l),
    }));
  };
  const uploadLessonMedia = async (idx, e) => {
    const file = e.target.files?.[0]; if (!file) return;
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/lms/files/upload", fd);
      patchLesson(idx, {storage_path: r.data.storage_path,
                        kind: file.type.startsWith("audio/") ? "audio"
                              : file.type.startsWith("video/") ? "video_upload"
                              : "pdf"});
      toast.success(`Uploaded ${file.name}`);
    } catch (err) {
      toast.error("Upload failed: " + (err?.response?.data?.detail || err.message));
    }
  };
  return (
    <div className="nb-card p-6 bg-[#A7F3D0] space-y-4" data-testid="lms-course-builder-tools">
      <div>
        <h3 className="display text-2xl font-bold">Course Builder — Bulk & AI Tools</h3>
        <p className="text-xs">Bulk import chapters via CSV or quiz questions via JSON. Generate lesson notes, question banks, and exam papers with Gemini AI. All upserts are idempotent.</p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <button onClick={()=>downloadTemplate("chapters")} className="nb-btn justify-center" data-testid="lms-dl-chapters-tpl">
          ↓ Chapters CSV Template
        </button>
        <label className="nb-btn justify-center cursor-pointer" data-testid="lms-upload-chapters">
          ↑ Upload Chapters CSV
          <input type="file" accept=".csv" hidden onChange={uploadChapters}/>
        </label>
        <button onClick={()=>downloadTemplate("quiz")} className="nb-btn justify-center" data-testid="lms-dl-quiz-tpl">
          ↓ Quiz JSON Template
        </button>
        <label className="nb-btn justify-center cursor-pointer" data-testid="lms-upload-quiz">
          ↑ Upload Quiz JSON
          <input type="file" accept=".json" hidden onChange={uploadQuiz}/>
        </label>
        <button onClick={exportCourse} className="nb-btn justify-center" data-testid="lms-export-course">
          ⬇ Export Course JSON
        </button>
        <button onClick={openAI} className="nb-btn justify-center bg-[#7C3AED] text-white" data-testid="lms-open-ai">
          ✨ Generate with AI
        </button>
      </div>
      {aiOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={()=>setAiOpen(false)}>
          <div className="nb-card bg-white p-6 max-w-5xl w-full max-h-[92vh] overflow-y-auto space-y-4"
               onClick={e=>e.stopPropagation()} data-testid="lms-ai-modal">
            <div className="flex justify-between items-start">
              <div>
                <h4 className="display text-xl font-bold">Build Course with Gemini AI</h4>
                <p className="text-xs">Pick target board(s)/grade/subject/chapter → attach optional source PDF/TXT → generate lessons, quiz, or exam → review and Push to the chapter.</p>
              </div>
              <button onClick={()=>setAiOpen(false)} className="nb-btn nb-btn-secondary text-xs">✕ Close</button>
            </div>
            {/* --- Target context (all pulled from Ponderr LMS tabs) --- */}
            <div className="grid md:grid-cols-4 gap-2 text-xs">
              <div className="relative">
                <div className="nb-label">Boards ({aiBoardCodes.length})</div>
                <button type="button" onClick={()=>setAiBoardsMenuOpen(x=>!x)}
                        className="nb-select w-full text-left" data-testid="ai-boards-toggle">
                  {aiBoardCodes.length === 0 ? "Pick boards…"
                    : aiBoardCodes.length === 1 ? aiBoardCodes[0]
                    : `${aiBoardCodes.length} boards`}
                </button>
                {aiBoardsMenuOpen && (
                  <div className="absolute z-30 mt-1 w-full nb-card bg-white p-2 max-h-56 overflow-y-auto space-y-1"
                       data-testid="ai-boards-menu">
                    {lmsBoards.map(b => (
                      <label key={b.code} className="flex items-center gap-2 cursor-pointer hover:bg-slate-100 p-1 rounded"
                             data-testid={`ai-board-${b.code}`}>
                        <input type="checkbox" checked={aiBoardCodes.includes(b.code)}
                               onChange={()=>toggleAiBoard(b.code)}/>
                        <span className="mono font-bold">{b.code}</span>
                        <span className="truncate">{b.name}</span>
                      </label>
                    ))}
                    <button type="button" onClick={()=>setAiBoardsMenuOpen(false)}
                            className="nb-btn nb-btn-ghost w-full mt-1 text-xs justify-center">Done</button>
                  </div>
                )}
              </div>
              <div>
                <div className="nb-label">Grade</div>
                <select className="nb-select" value={aiGrade} onChange={e=>setAiGrade(e.target.value)} data-testid="ai-grade">
                  {lmsGrades.map(g => <option key={g} value={g}>{g}</option>)}
                </select>
              </div>
              <div>
                <div className="nb-label">Subject</div>
                <select className="nb-select" value={aiSubjectId} onChange={e=>setAiSubjectId(e.target.value)} data-testid="ai-subject">
                  {lmsSubjects.map(s => <option key={s.subject_id} value={s.subject_id}>{s.name} ({s.code})</option>)}
                </select>
              </div>
              <div>
                <div className="nb-label">Chapter <span className="text-rose-600">*</span></div>
                <select className="nb-select" value={aiChapterId}
                        onChange={e=>setAiChapterId(e.target.value)} data-testid="ai-chapter">
                  <option value="">— Pick chapter —</option>
                  {aiChapters.map(c => (
                    <option key={c.chapter_id} value={c.chapter_id}>{c.order || ""}. {c.title}</option>
                  ))}
                </select>
              </div>
            </div>
            {!aiChapterId && (
              <div className="text-xs text-rose-600 mono">
                ⚠ Chapter is required. Add one in the Curriculum Manager first, then reopen this dialog.
              </div>
            )}

            {/* --- Optional source material --- */}
            <div className="nb-card-sm p-3 bg-[#FEF3C7] space-y-2">
              <div className="flex items-center justify-between gap-2">
                <div>
                  <div className="font-bold text-sm">Source material (optional)</div>
                  <p className="text-[11px]">Attach a PDF or TXT so Gemini bases the output on your content instead of general knowledge. Only the first ~12,000 chars are used to keep response time under 90 s.</p>
                </div>
                <label className="nb-btn cursor-pointer" data-testid="ai-src-upload">
                  {srcLoading ? "Extracting…" : (srcFile ? "↺ Replace file" : "↑ Upload PDF/TXT")}
                  <input type="file" accept=".pdf,.txt,.md" hidden onChange={uploadSource}/>
                </label>
              </div>
              {srcFile && (
                <div className="text-[11px] mono flex justify-between items-center gap-2">
                  <span>{srcFile.name} · {srcText.length} chars extracted</span>
                  <button type="button" onClick={()=>{setSrcFile(null); setSrcText("");}}
                          className="nb-btn nb-btn-ghost text-[10px]">✕ Remove</button>
                </div>
              )}
            </div>

            {/* --- Three generation actions in one row --- */}
            <div className="grid md:grid-cols-3 gap-2">
              <div className="nb-card-sm p-3 bg-[#DBEAFE] space-y-2">
                <div className="font-bold text-sm">1. Lesson Notes</div>
                <input type="number" min="1" max="3" className="nb-input w-full text-xs"
                       placeholder="How many lessons? (max 3)" value={lessonCount}
                       onChange={e=>setLessonCount(e.target.value)} data-testid="ai-lesson-count"/>
                <button onClick={()=>runGen("curriculum_lesson", lessonCount, setLessonOut)}
                        disabled={loading==="curriculum_lesson" || !aiChapterId}
                        className="nb-btn bg-[#7C3AED] text-white w-full justify-center text-xs"
                        data-testid="ai-gen-lessons">
                  {loading==="curriculum_lesson" ? "Generating…" : "✨ Generate Lesson Notes"}
                </button>
              </div>
              <div className="nb-card-sm p-3 bg-[#DCFCE7] space-y-2">
                <div className="font-bold text-sm">2. Quiz</div>
                <input type="number" min="1" max="10" className="nb-input w-full text-xs"
                       placeholder="How many questions? (max 10)" value={quizCount}
                       onChange={e=>setQuizCount(e.target.value)} data-testid="ai-quiz-count"/>
                <button onClick={()=>runGen("question_bank", quizCount, setQuizOut)}
                        disabled={loading==="question_bank" || !aiChapterId}
                        className="nb-btn bg-[#7C3AED] text-white w-full justify-center text-xs"
                        data-testid="ai-gen-quiz">
                  {loading==="question_bank" ? "Generating…" : "✨ Generate Quiz"}
                </button>
              </div>
              <div className="nb-card-sm p-3 bg-[#FCE7F3] space-y-2">
                <div className="font-bold text-sm">3. Exam Paper</div>
                <div className="text-[11px] text-slate-600">Full exam with sections + rubric. Preview only — no auto-push yet.</div>
                <button onClick={()=>runGen("exam_paper", quizCount, setExamOut)}
                        disabled={loading==="exam_paper" || !aiChapterId}
                        className="nb-btn bg-[#7C3AED] text-white w-full justify-center text-xs"
                        data-testid="ai-gen-exam">
                  {loading==="exam_paper" ? "Generating…" : "✨ Generate Exam Paper"}
                </button>
              </div>
            </div>

            {/* --- Lessons preview & push --- */}
            {lessonOut?.lessons?.length ? (
              <div className="nb-card-sm p-3 bg-white space-y-3" data-testid="ai-lessons-preview">
                <div className="flex justify-between items-center">
                  <div className="font-bold text-sm">Generated Lessons ({lessonOut.lessons.length})</div>
                  <button onClick={pushLessons} disabled={pushing==="lessons"}
                          className="nb-btn bg-[#059669] text-white text-xs"
                          data-testid="ai-push-lessons">
                    {pushing==="lessons" ? "Pushing…" : "↳ Push Lessons → Chapter"}
                  </button>
                </div>
                {lessonOut.lessons.map((L, i) => (
                  <div key={i} className="nb-card-sm p-2 bg-slate-50 space-y-2">
                    <div className="grid md:grid-cols-[1fr_100px] gap-2">
                      <input className="nb-input text-xs" placeholder="Lesson title"
                             value={L.title||""} onChange={e=>patchLesson(i, {title:e.target.value})}/>
                      <input type="number" className="nb-input text-xs" placeholder="min"
                             value={L.duration_min||""}
                             onChange={e=>patchLesson(i, {duration_min: parseInt(e.target.value,10)||null})}/>
                    </div>
                    <textarea className="nb-textarea w-full text-xs font-mono" rows={6}
                              value={L.notes_html||""}
                              onChange={e=>patchLesson(i, {notes_html:e.target.value})}/>
                    <div className="grid md:grid-cols-3 gap-2 text-xs">
                      <input className="nb-input text-xs" placeholder="YouTube URL (optional)"
                             value={L.youtube_url||""}
                             onChange={e=>patchLesson(i, {youtube_url:e.target.value})}/>
                      <input className="nb-input text-xs" placeholder="Uploaded media path (optional)"
                             value={L.storage_path||""} readOnly/>
                      <label className="nb-btn nb-btn-tertiary text-xs cursor-pointer justify-center">
                        ↑ Attach PDF/Audio/Video
                        <input type="file" accept=".pdf,audio/*,video/*" hidden
                               onChange={e=>uploadLessonMedia(i, e)}/>
                      </label>
                    </div>
                  </div>
                ))}
                <p className="text-[11px] text-slate-500">Attachments are optional — you (or an educator) can edit lessons in the Course Builder later to add audio/PDF/video before delivery to students.</p>
              </div>
            ) : null}

            {/* --- Quiz preview & push --- */}
            {quizOut?.questions?.length ? (
              <div className="nb-card-sm p-3 bg-white space-y-3" data-testid="ai-quiz-preview">
                <div className="flex justify-between items-center">
                  <div className="font-bold text-sm">Generated Questions ({quizOut.questions.length})</div>
                  <button onClick={pushQuiz} disabled={pushing==="quiz"}
                          className="nb-btn bg-[#059669] text-white text-xs"
                          data-testid="ai-push-quiz">
                    {pushing==="quiz" ? "Pushing…" : "↳ Push Quiz → Chapter"}
                  </button>
                </div>
                <textarea className="nb-textarea w-full font-mono text-xs" rows={12}
                          value={JSON.stringify(quizOut, null, 2)}
                          onChange={e=>{ try { setQuizOut(JSON.parse(e.target.value)); } catch { /* ignore */ } }}
                          data-testid="ai-quiz-json"/>
              </div>
            ) : null}

            {/* --- Exam preview (no auto-push) --- */}
            {examOut ? (
              <div className="nb-card-sm p-3 bg-white space-y-3" data-testid="ai-exam-preview">
                <div className="flex justify-between items-center">
                  <div className="font-bold text-sm">Generated Exam Paper</div>
                  <button onClick={()=>{navigator.clipboard.writeText(JSON.stringify(examOut, null, 2)); toast.success("Copied — paste into Exams tab");}}
                          className="nb-btn text-xs">↳ Copy to Clipboard</button>
                </div>
                <textarea className="nb-textarea w-full font-mono text-xs" rows={14}
                          value={JSON.stringify(examOut, null, 2)} readOnly
                          data-testid="ai-exam-json"/>
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}

function LMS() {
  const [rows, setRows] = useState([]);
  const [settings, setSettings] = useState({grades:[]});
  const [f, setF] = useState({title:"", grade:"G10", html_content:""});
  const [editing, setEditing] = useState(null); // bundle being edited
  const load = async () => {
    setRows((await api.get("/lms/bundles")).data);
    setSettings((await api.get("/settings")).data);
  };
  useEffect(()=>{ load(); }, []);
  const create = async (e) => { e.preventDefault();
    if (!f.html_content) { toast.error("Paste HTML content or upload .html file"); return; }
    await api.post("/lms/bundles", f); setF({title:"",grade:f.grade,html_content:""}); toast.success("Bundle uploaded"); load();
  };
  const del = async (id) => {
    if (!window.confirm("Delete this bundle for all students of this grade?")) return;
    await api.delete(`/lms/bundles/${id}`); load();
  };
  const onFile = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 2*1024*1024) { toast.error("Max 2MB"); return; }
    const r = new FileReader(); r.onload = ()=> setF(x=>({...x, html_content:r.result})); r.readAsText(file);
  };
  const startEdit = async (b) => {
    // Load full HTML content (list endpoint may not return full body — refetch)
    try {
      const r = await api.get(`/lms/content/${b.bundle_id}`, {responseType: "text"});
      setEditing({...b, html_content: r.data});
    } catch { setEditing({...b, html_content: b.html_content || ""}); }
  };
  const onEditFile = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 2*1024*1024) { toast.error("Max 2MB"); return; }
    const r = new FileReader(); r.onload = () => setEditing(x => ({...x, html_content: r.result})); r.readAsText(file);
  };
  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editing.html_content) return toast.error("HTML content required");
    try {
      await api.patch(`/lms/bundles/${editing.bundle_id}`, {
        title: editing.title, grade: editing.grade, html_content: editing.html_content,
      });
      toast.success("Bundle updated for all students of this grade");
      setEditing(null); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  // Group by grade
  const byGrade = rows.reduce((a,b)=>{(a[b.grade]=a[b.grade]||[]).push(b); return a;},{});
  return <div className="space-y-6">
    <GradesManager/>
    <CourseBuilderTools/>
    <BoardsManagement/>
    <MasterSubjectsManager/>
    <CurriculumManager/>
    <QuestionBankManager/>
    <ExamsManager/>
    <div className="nb-card p-4 bg-slate-100 text-xs mono">
      Legacy Course Bundles (single-file HTML) — retained for backwards compatibility. New content should go through the Curriculum Manager above.
    </div>
    <form onSubmit={create} className="nb-card p-6 bg-[#C4B5FD] grid lg:grid-cols-3 gap-3">
      <h3 className="display text-xl font-bold lg:col-span-3">Upload Course Bundle</h3>
      <input className="nb-input" placeholder="Title" required value={f.title} onChange={e=>setF({...f,title:e.target.value})} data-testid="lms-new-title"/>
      <select className="nb-select" value={f.grade} onChange={e=>setF({...f,grade:e.target.value})} data-testid="lms-new-grade">
        {settings.grades?.map(g=><option key={g} value={g}>{g}</option>)}
      </select>
      <input type="file" accept=".html,.htm" onChange={onFile} className="nb-input text-xs"/>
      <textarea className="nb-textarea lg:col-span-3" rows={6} placeholder="…or paste single-file HTML (inline CSS/JS) here" value={f.html_content} onChange={e=>setF({...f,html_content:e.target.value})} data-testid="lms-new-html"/>
      <button className="nb-btn lg:col-span-3 justify-center" data-testid="lms-new-submit"><Plus size={16}/>Upload Bundle</button>
    </form>
    <div className="space-y-4">
      {(settings.grades||[]).map(g=>(
        <div key={g} className="nb-card p-4">
          <h3 className="display text-lg font-bold mb-3">{g}</h3>
          <div className="grid sm:grid-cols-3 gap-3">
            {(byGrade[g]||[]).map(b=>(
              <div key={b.bundle_id} className="nb-card-sm p-3 bg-white flex justify-between items-center"
                   data-testid={`lms-row-${b.bundle_id}`}>
                <div className="min-w-0"><div className="font-bold text-sm truncate">{b.title}</div><div className="text-xs mono text-slate-500 truncate">{b.bundle_id}</div></div>
                <div className="flex gap-1 shrink-0">
                  <button onClick={()=>startEdit(b)} className="nb-btn nb-btn-secondary px-2 py-1"
                          data-testid={`lms-edit-${b.bundle_id}`}>
                    <Pencil size={12}/>
                  </button>
                  <button onClick={()=>del(b.bundle_id)} className="nb-btn nb-btn-danger px-2 py-1"
                          data-testid={`lms-delete-${b.bundle_id}`}>
                    <Trash2 size={12}/>
                  </button>
                </div>
              </div>
            ))}
            {(byGrade[g]||[]).length===0 && <div className="text-xs text-slate-500">No bundles for this grade</div>}
          </div>
        </div>
      ))}
    </div>
    {editing && (
      <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4"
           onClick={()=>setEditing(null)}>
        <form onSubmit={saveEdit}
              className="nb-card p-6 bg-white max-w-2xl w-full space-y-3 max-h-[90vh] overflow-y-auto"
              onClick={e=>e.stopPropagation()}
              data-testid="lms-edit-modal">
          <h3 className="display text-xl font-bold">Edit Course Bundle</h3>
          <p className="text-xs">Changes push live to every student of this grade.</p>
          <input className="nb-input" placeholder="Title" required
                 value={editing.title||""} onChange={e=>setEditing({...editing, title:e.target.value})}
                 data-testid="lms-edit-title"/>
          <select className="nb-select" value={editing.grade}
                  onChange={e=>setEditing({...editing, grade:e.target.value})}>
            {(settings.grades||[]).map(g => <option key={g} value={g}>{g}</option>)}
          </select>
          <input type="file" accept=".html,.htm" onChange={onEditFile} className="nb-input text-xs"/>
          <textarea className="nb-textarea" rows={10}
                    value={editing.html_content||""}
                    onChange={e=>setEditing({...editing, html_content:e.target.value})}
                    data-testid="lms-edit-html"
                    placeholder="Single-file HTML (inline CSS/JS)"/>
          <div className="flex gap-2">
            <button type="button" onClick={()=>setEditing(null)}
                    className="nb-btn nb-btn-ghost flex-1 justify-center">Cancel</button>
            <button className="nb-btn flex-1 justify-center" data-testid="lms-edit-save">Save</button>
          </div>
        </form>
      </div>
    )}
  </div>;
}
